import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Link } from 'react-router-dom';
import { useUrlString, useUrlNumberOpt, useUrlStringOrNull, useUrlDay } from '../../hooks/useUrlState';
import { useUrlTab, useUrlParam } from '../../hooks/useUrlTab';
import {
  Card, Select, Button, Segmented, StatCard, Modal, Input, InputNumber, Switch,
  EmptyState, Tooltip, InfoTooltip, message, Spin, DatePicker, FilterResetButton,
} from '../../ui';
import {
  CalendarClock, AlertTriangle, CheckCircle2, CircleSlash, MapPin, Wand2,
  Plus, X, Store, User, Download, Search, TriangleAlert, ChevronRight, ChevronLeft, History,
  CalendarPlus, ExternalLink, ShoppingCart, Users, ClipboardList, PartyPopper, SearchX, HeartPulse, Pencil, Undo2,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission, useAuthStore } from '../../store/auth';
import { ExportDialog } from '../../components/ExportDialog';
import { TwDataTable as DataTable, DataTableColumn, Pill } from '../../components/TwDataTable';
import { useStaffTerritories } from '../../lib/useStaffTerritories';
import { RoleBadge, roleHue, rolesOfUser, staffPickerOptions } from '../../components/RoleBadge';
import { TERRITORY_LEVEL } from '../../components/TerritoryChips';
import { hueOption } from '../../components/FilterPanel';
import { PALETTE, TAG_TONE_HUE, filterTint } from '../../lib/palette';
import { TerritoryLevelTag } from '../../components/TerritoryChips';
import { UserLink } from '../../components/EntityLinks';
import { formatIstDate, formatIstTime } from '../../lib/time';
import { inr } from '../../lib/format';

/* ────────────────────────────────────────────────────────────────────────────
 * BEAT COVERAGE — the rule, the plan, and who has gone unseen.
 *
 * Three sections, rendered as TABS OF THE VISITS PAGE rather than a page of their own: "who did we
 * visit" and "who is still owed a visit" are two halves of one question. One component driven by a
 * `section` prop, so switching tabs keeps the filters the user already set.
 * ──────────────────────────────────────────────────────────────────────────── */

export type CoverageSectionKey = 'coverage' | 'schedule' | 'norms';

type Audience = 'RETAILER' | 'FARMER' | 'BOTH';

/** Dropdown 1 — how many visits per window. "Custom" reveals a number box. */
const FREQUENCY = [
  { value: 1, label: 'Once' },
  { value: 2, label: 'Twice' },
  { value: 3, label: 'Three Times' },
  { value: 4, label: 'Four Times' },
  { value: 0, label: 'Custom…' },
];

/** Dropdown 2 — the window. A year is stored as 12 months so every cycle is one unit of arithmetic. */
/** A `limit` larger than any book: the due list is computed whole on the server, so asking for all of it costs nothing extra. */
const UNCAPPED = 100_000;

const PERIOD: Array<{ key: string; label: string; cycleLength: number; cycleUnit: 'WEEK' | 'MONTH' }> = [
  { key: 'week', label: 'Week', cycleLength: 1, cycleUnit: 'WEEK' },
  { key: 'month', label: 'Month', cycleLength: 1, cycleUnit: 'MONTH' },
  { key: '2m', label: '2 Months', cycleLength: 2, cycleUnit: 'MONTH' },
  { key: '3m', label: '3 Months', cycleLength: 3, cycleUnit: 'MONTH' },
  { key: '6m', label: '6 Months', cycleLength: 6, cycleUnit: 'MONTH' },
  { key: 'year', label: 'Year', cycleLength: 12, cycleUnit: 'MONTH' },
];

/**
 * DISPLAY LANGUAGE. The API's vocabulary is unchanged — this maps its values to the words a Sales
 * Head actually uses, in one place, so no screen invents its own wording for the same state.
 */
const COVERAGE_STATE: Record<string, { color: string; label: string }> = {
  COVERED: { color: 'green', label: 'Coverage Completed' },
  PARTIAL: { color: 'gold', label: 'Partly Covered' },
  /*
   * NOT "Overdue Visit". This state means nothing has been counted in the window YET — a customer
   * with 0 of 2 done and three weeks still to run is in it, and was wearing a red "Overdue Visit"
   * beside a Next Due column reading "due in 82d". Overdue is a different test entirely
   * (`overdueDays > 0`, past the gap the rule allows) and the Next Due column is where it is made.
   * The three states are a PARTITION of the window's progress, so they are named as one.
   */
  NOT_VISITED: { color: 'red', label: 'Not Visited Yet' },
  /* The window has CLOSED with nothing counted — only ever on a past window. Separate from
     NOT_VISITED, which still has time left; the exports used to call both "Missed". */
  MISSED: { color: 'red', label: 'Missed' },
  EXEMPT: { color: 'purple', label: 'Exempted' },
  NO_NORM: { color: 'default', label: 'No Visit Schedule' },
};

const STOP_STATUS: Record<string, { color: string; label: string }> = {
  PLANNED: { color: 'blue', label: 'Planned Visit' },
  STARTED: { color: 'gold', label: 'In Progress' },
  COMPLETED: { color: 'green', label: 'Completed' },
  /*
   * A PLAN that was not kept — which is not the same fact as a customer being overdue, and calling
   * both "Overdue Visit" put the two on one screen: a week of red "Overdue Visit" stops beside a
   * panel reporting that nobody was owed a call. Both were true. A rep can miss a Monday stop for a
   * dealer whose rule is met, and a dealer can be months overdue with nothing ever planned.
   */
  MISSED: { color: 'red', label: 'Missed' },
  CANCELLED: { color: 'default', label: 'Cancelled' },
};

/**
 * The one urgency scale the whole module reads by, so a red row means the same thing in the due list
 * as it does on the calendar: RED overdue · ORANGE due soon · BLUE planned · GREEN done.
 *
 * "Due soon" is deliberately relative to the customer's OWN gap, not a fixed number of days: three
 * days out matters on a twice-a-month rule and means nothing on a twice-a-year one.
 */
type Urgency = 'overdue' | 'soon' | 'planned' | 'done';
const urgencyOf = (overdueDays: number | null, intervalDays: number | null): Urgency => {
  if (overdueDays == null) return 'planned';
  if (overdueDays > 0) return 'overdue';
  const window = Math.max(2, Math.round((intervalDays ?? 30) * 0.25));
  return overdueDays >= -window ? 'soon' : 'planned';
};
const URGENCY_BAR: Record<Urgency, string> = {
  overdue: 'bg-danger-500',
  soon: 'bg-warning-500',
  planned: 'bg-sky-500',
  done: 'bg-brand-500',
};

/**
 * "Overdue by 6 days" / "Due in 9 days".
 *
 * The API returns one signed number, because one number is what the sort needs. A bare `-9` would
 * make a rep with nine days of slack look like the worst row on the page, so the sign is spelled out
 * in words and only a genuine overdue is coloured.
 */
function OverdueCell({ days }: { days: number | null }) {
  if (days == null) return <span className="text-subtle">—</span>;
  if (days > 0) {
    const hard = days > 14;
    return <span className={hard ? 'font-semibold text-danger-600' : 'text-warning-600'}>{days}d overdue</span>;
  }
  if (days === 0) return <span className="text-warning-600">due today</span>;
  return <span className="text-subtle">due in {Math.abs(days)}d</span>;
}

/**
 * "Planned Sat 12 Sep" — what is ALREADY on the calendar for a customer who is still owed a visit.
 *
 * The due row counts COMPLETED visits ("0/2 done"), so planning a call does not take the customer
 * off the list — and it should not: a plan is not a visit. But with nothing said, a row you planned
 * ten seconds ago reads exactly like one nobody has touched, and the filled day chip was the only
 * trace of the decision. These lines state the LIVE state of the stops the loaded window holds.
 *
 * COMPLETED stops are left out on purpose: "0/2 done" on the line above is already that fact, and
 * one screen must not print the same figure twice. A CANCELLED stop is not a commitment at all.
 */
const STOP_NOTE = [
  { status: 'STARTED', verb: 'Visit in progress', tone: 'text-warning-600 dark:text-harvest-300', Icon: MapPin },
  { status: 'MISSED', verb: 'Missed', tone: 'text-danger-600 dark:text-danger-500', Icon: TriangleAlert },
  { status: 'PLANNED', verb: 'Planned', tone: 'text-sky-600 dark:text-sky-400', Icon: CalendarClock },
] as const;

/** One line per state, days named and in order: "Planned Wed 09 Sep, Sat 12 Sep". */
function scheduleNotes(stops: Array<{ status: string; planDate: string }>) {
  return STOP_NOTE.flatMap(({ status, verb, tone, Icon }) => {
    const days = stops.filter((s) => s.status === status).map((s) => s.planDate).sort();
    if (!days.length) return [];
    return [{ key: status, tone, Icon, text: `${verb} ${days.map((d) => dayjs(d).format('ddd DD MMM')).join(', ')}` }];
  });
}

/**
 * "1–30 Sep" / "3 Sep – 2 Dec" — the window a progress figure was counted over.
 *
 * It rides WITH the figure everywhere the figure appears. "0/2" on its own is a fraction of a period
 * the reader has to guess, and the period is not the calendar month they would guess: it is the
 * rule's own cycle, which starts on the rule's anchor date and is as long as the rule says.
 */
function windowSpan(start?: string | null, end?: string | null): string | null {
  if (!start || !end) return null;
  const a = dayjs(start);
  const b = dayjs(end);
  if (!a.isValid() || !b.isValid()) return null;
  const yr = (d: Dayjs) => `’${d.format('YY')}`;
  // A window that crosses New Year gets BOTH years, always: "3 Dec – 2 Mar" leaves the reader to
  // work out that the December is last year's, which is exactly the guess this line exists to stop.
  if (a.year() !== b.year()) return `${a.format('D MMM')} ${yr(a)} – ${b.format('D MMM')} ${yr(b)}`;
  // Within one year the year is printed only when it is not the current one — it matters twelve
  // windows back and is noise the rest of the time.
  const tail = b.year() === dayjs().year() ? '' : ` ${yr(b)}`;
  if (a.month() === b.month()) return `${a.format('D')}–${b.format('D MMM')}${tail}`;
  return `${a.format('D MMM')} – ${b.format('D MMM')}${tail}`;
}

/** The steps the window picker offers. Sent to the server so both ends date the same list. */
const WINDOW_STEPS = [0, 1, 2, 3, 4, 5, 6, 12];

type WindowSpan = { start: string; end: string; customers: number; beforeRule: boolean; audiences?: string[] };

const stepName = (n: number) => (n === 0 ? 'This window' : n === 1 ? 'Previous window' : `${n} windows back`);

/**
 * What a step actually covers, in dates.
 *
 * "2 windows back" is not a period anyone can check a figure against — you would have to know the
 * rule's anchor date to work out what it means, and two rules on the same screen mean two different
 * answers. So each option names its dates, and when a step covers more than one span it names WHO is
 * on each: "1–31 Aug retailers · 3 Jun – 2 Sep farmers" is the sentence that explains the split,
 * where "2 ranges" would only announce it.
 */
function spansText(spans: WindowSpan[], showWho = true): string {
  if (!spans.length) return '';
  // Naming the audience earns its place only while both are on the page: with "Retailers only"
  // already chosen, "1–30 Sep retailers" says the word twice and the date once.
  const who = (s: WindowSpan) => (showWho && s.audiences?.length === 1
    ? (s.audiences[0] === 'RETAILER' ? ' retailers' : ' farmers')
    : '');
  const shown = spans.slice(0, 2).map((s) => `${windowSpan(s.start, s.end)}${who(s)}`);
  const rest = spans.length - shown.length;
  return [...shown, rest > 0 ? `+${rest} more` : null].filter(Boolean).join(' · ');
}

/**
 * A row action, in the hue of the act it performs.
 *
 * Four identical grey glyphs on every row read as one decoration rather than four different acts, so
 * each takes the colour that thing already wears on this page: the visit record the info blue the
 * planner uses, a new call the brand green, an exemption the plum of its own status pill. Opening
 * the customer stays neutral — it is navigation, not an act on the row.
 */
const ACTION_TONE = {
  info: 'text-sky-600 hover:bg-sky-500/12 dark:text-sky-400',
  go: 'text-brand-600 hover:bg-brand-500/12 dark:text-brand-400',
  stop: 'text-plum-500 hover:bg-plum-500/12 dark:text-plum-400',
  plain: 'text-subtle hover:bg-surface-3 hover:text-fg',
} as const;

function RowAction({ tone, title, label, icon, onClick, to }: {
  tone: keyof typeof ACTION_TONE; title: string; label: string; icon: any;
  onClick?: () => void; to?: string;
}) {
  const cls = `inline-flex size-8 items-center justify-center rounded-md transition-colors ${ACTION_TONE[tone]}`;
  return (
    <Tooltip title={title}>
      {to
        ? <Link to={to} aria-label={label} className={cls}>{icon}</Link>
        : <button type="button" aria-label={label} onClick={onClick} className={cls}>{icon}</button>}
    </Tooltip>
  );
}

const audienceIcon = (t: string) =>
  t === 'RETAILER' ? <Store className="size-3.5 shrink-0" /> : <User className="size-3.5 shrink-0" />;

/** Retailers have a detail page; farmers do not, so their link goes to the register they live in. */
const customerHref = (r: any) =>
  r.customerType === 'RETAILER' ? `/retailers/${r.customerId}` : '/people?tab=farmers';

export function CoverageSection({ section }: { section: CoverageSectionKey }) {
  const qc = useQueryClient();

  const canPlan = usePermission('COVERAGE.PLAN.EDIT');
  const canSchedule = usePermission('COVERAGE.SCHEDULE.EDIT');
  const canExempt = usePermission('COVERAGE.EXEMPT');
  // Reassignment is an edit of the CUSTOMER, so it borrows the customer's own permission rather
  // than inventing a coverage one — the same gate the People register enforces.
  const canEditRetailer = usePermission('RETAILER.EDIT');
  const canEditFarmer = usePermission('FARMER.EDIT');
  const canExport = usePermission('COVERAGE.EXPORT');
  const canViewUsers = usePermission('USER.VIEW');
  const me = useAuthStore((st) => st.user);

  // ── Filters ─────────────────────────────────────────────────────────────────────────────────
  const terr = useStaffTerritories();
  const [state, setState] = useUrlStringOrNull('state');
  const [district, setDistrict] = useUrlStringOrNull('district');
  const [beat, setBeat] = useUrlStringOrNull('beat');
  const [audience, setAudience] = useUrlString<Audience | undefined>('audience', undefined);
  const [repId, setRepId] = useUrlString<string | undefined>('rep', undefined);
  const [q, setQ] = useUrlString('q');
  const [noVisitDays, setNoVisitDays] = useUrlNumberOpt('noVisit');
  /**
   * Which window to read: 0 / absent is the one today falls in, 1 is the one before it, and so on —
   * in each customer's OWN cycle, because a monthly rule and a quarterly one do not share a "last
   * month". Every figure on the page then reports what was true on that window's last day.
   */
  const [windowBack, setWindowBack] = useUrlNumberOpt('window');
  const pastWindow = (windowBack ?? 0) > 0;
  /** Which KPI tile is driving the list. ONE value, so two tiles can never both look selected. */
  const [focus, setFocus] = useUrlStringOrNull<'COVERED' | 'NO_NORM' | 'due' | 'overdue'>('focus');
  const territoryId = beat ?? district ?? state ?? undefined;

  /** Set the whole state → district → beat chain from any node, so a drill-down lands consistently. */
  const drillTo = (id: string) => {
    const chain: any[] = [];
    let cur = terr.nodes.get(String(id));
    for (let hop = 0; cur && hop < 8; hop++) { chain.push(cur); cur = cur.parentId ? terr.nodes.get(cur.parentId) : undefined; }
    setState(chain.find((n) => n.level === 'state')?.id ?? null);
    setDistrict(chain.find((n) => n.level === 'district')?.id ?? null);
    setBeat(chain.find((n) => n.level === 'beat')?.id ?? null);
  };
  const clearFilters = () => {
    setState(null); setDistrict(null); setBeat(null);
    setAudience(undefined); setRepId(undefined); setQ(''); setNoVisitDays(undefined); setFocus(null);
    setWindowBack(undefined);
  };

  /*
   * "No Rule: 3" — and then what? A count with no way through is a dead end: the reader is told
   * three customers here are uncovered by any rule and left to find them by hand. These two
   * handlers are the whole route from the number to the fix.
   *
   * `showNoRule` opens the count's own slice: the same territory, narrowed to exactly those
   * customers, on the grain that lists customers. `goSetRule` carries the territory across to the
   * Visit Scheduler and opens the rule form already pointed at it — the tab used to be named in
   * words ("Open the Visit Scheduler tab to set one") and the reader had to re-find the territory
   * in a cascade of thirty states once they got there.
   *
   * Both writes in each handler land together: `useUrlTab`/`useUrlParam` share one write buffer, so
   * the tab move and the filter that makes it meaningful cannot race.
   */
  const [, setSection] = useUrlTab('section', ['visits', 'coverage', 'schedule', 'norms'] as const, 'coverage');
  const [newRuleFor, setNewRuleFor] = useUrlParam('newRule');
  const showNoRule = (tid: string) => { drillTo(tid); setFocus('NO_NORM'); setGrain('customer'); };
  const goSetRule = (tid?: string | null) => { setNewRuleFor(tid ? String(tid) : undefined); setSection('norms'); };

  const { data: users } = useQuery({
    queryKey: ['field-staff-users'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON,SALES_HEAD' }),
    enabled: canViewUsers,
  });
  const staff = (users?.data ?? []) as any[];

  /*
   * WHO, as people rather than as a list of strings: grouped Heads / Persons, each option wearing its
   * own role pill. A picker that mixes a Sales Head between two reps and spells neither is a
   * word-by-word read — and this page is ABOUT who is answerable for a beat.
   */
  const staffOptions = useMemo(() => staffPickerOptions(staff), [staff]);
  // A SET control wears the colour of what it selected — here, the chosen person's role.
  const repTint = useMemo(() => {
    if (!repId) return null;
    const u = staff.find((s: any) => String(s.id) === String(repId));
    return roleHue(u?.role?.code ?? u?.roleCode ?? null);
  }, [repId, staff]);

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (territoryId) p.territoryId = territoryId;
    if (audience) p.audience = audience;
    if (repId) p.repId = repId;
    if (q.trim()) p.q = q.trim();
    if (focus === 'COVERED' || focus === 'NO_NORM') p.state = focus;
    if (focus === 'due') p.due = '1';
    if (focus === 'overdue') p.overdue = '1';
    if (windowBack) p.window = String(windowBack);
    return p;
  }, [territoryId, audience, repId, q, focus, windowBack]);

  // The KPI band is a SEPARATE fetch that drops the tile filters: the tiles ARE those controls, so
  // narrowing them by their own selection would collapse every sibling toward zero. The WINDOW is
  // kept: the band and the list must be counting the same cycle or the tiles describe another page.
  const bandParams = useMemo(() => {
    const { state: _s, due: _d, overdue: _o, ...rest } = params;
    return rest;
  }, [params]);

  /*
   * The picker's own dates. A separate read because it has to name windows the page is NOT showing,
   * and it drops the tile filters for the same reason the band does — the span a customer is on does
   * not change because you clicked "Overdue".
   */
  const windowScopeParams = useMemo(() => {
    const { window: _w, state: _s, due: _d, overdue: _o, ...rest } = params;
    return { ...rest, steps: WINDOW_STEPS.join(',') };
  }, [params]);

  const { data: windowSteps } = useQuery({
    queryKey: ['coverage-windows', windowScopeParams],
    queryFn: () => request<Array<{ back: number; spans: WindowSpan[] }>>({ url: '/coverage/windows', params: windowScopeParams }),
    enabled: section === 'coverage',
    placeholderData: keepPreviousData,
  });
  const spansFor = (back: number): WindowSpan[] => windowSteps?.find((s) => s.back === back)?.spans ?? [];

  const { data: summary } = useQuery({
    queryKey: ['coverage-summary', bandParams],
    queryFn: () => request<any>({ url: '/coverage/summary', params: bandParams }),
    enabled: section === 'coverage',
    placeholderData: keepPreviousData,
  });

  const [grain, setGrain] = useUrlTab('grain', ['customer', 'territory', 'rep'] as const, 'customer');

  /*
   * `keepPreviousData` keeps the table populated while a FILTER changes, which is what you want —
   * but the three grains return different shapes, and holding the previous payload across a GRAIN
   * change rendered territory rows under the rep grain's `rowKey`. So the payload is tagged with the
   * grain it was fetched for, and rows only reach the table when the two agree.
   */
  const { data: payload, isFetching } = useQuery({
    queryKey: ['coverage-rows', grain, params, noVisitDays],
    queryFn: async () => ({
      grain,
      rows: await request<any[]>({
        url: noVisitDays && grain === 'customer' ? '/coverage/no-visit'
          : grain === 'customer' ? '/coverage/customers'
          : grain === 'territory' ? '/coverage/territories' : '/coverage/reps',
        params: noVisitDays && grain === 'customer' ? { ...params, days: String(noVisitDays) } : params,
      }),
    }),
    enabled: section === 'coverage',
    placeholderData: keepPreviousData,
  });
  const stale = payload?.grain !== grain;
  const rows = stale ? [] : payload?.rows;
  const isLoading = isFetching || stale;

  /** True when `id` sits anywhere under `ofId` in the territory tree. Depth-guarded like `chain`. */
  const isUnder = (id: string | null | undefined, ofId: string) => {
    let cur = id ? terr.nodes.get(String(id)) : undefined;
    for (let hop = 0; cur && hop < 8; hop++) {
      if (cur.parentId === String(ofId)) return true;
      cur = cur.parentId ? terr.nodes.get(cur.parentId) : undefined;
    }
    return false;
  };

  /**
   * Drilling into a territory row lands wherever there is something NEW to read.
   *
   * A row on this grain counts the customers attached to THAT territory, so the list mixes levels:
   * a district appears beside the beats under it. Narrowing to a territory that has no other row
   * beneath it therefore returned the clicked row and nothing else — click Nagpur, get Nagpur —
   * and its 19 customers were reachable only by knowing to switch the grain by hand. A beat was
   * already special-cased for exactly this reason; what actually matters is not the level but
   * whether anything on this page sits below the row, so that is what is asked.
   */
  const drillInto = (id: string, level?: string | null) => {
    drillTo(id);
    const somethingBelow = (rows ?? []).some((o: any) => isUnder(o.territoryId, id));
    if (level === 'beat' || !somethingBelow) setGrain('customer');
  };

  /*
   * Export.
   *
   * This used to be `window.open('/v1/coverage/customers/export.csv?…')`. A browser navigation carries
   * no Authorization header and this app authenticates with a bearer token held in localStorage, so
   * the new tab reliably landed on the API's 401 JSON rather than a file — the button had never
   * worked. It also always fetched the CUSTOMER register, whichever of the three views you were
   * reading, and dropped the no-visit lookback.
   *
   * The shared dialog sends the request through the API client (token attached), offers PDF / Excel /
   * CSV, and carries the live filters AND the grain, so the file matches the screen it came from.
   */
  const [exportOpen, setExportOpen] = useState(false);
  const exportParams = useMemo(
    () => ({ ...params, grain, ...(noVisitDays ? { days: String(noVisitDays) } : {}) }),
    [params, grain, noVisitDays],
  );

  // ── Drawers / dialogs ───────────────────────────────────────────────────────────────────────
  const [exemptTarget, setExemptTarget] = useState<any | null>(null);
  const [exemptReason, setExemptReason] = useState('');
  /** The exemption's last day; empty = until someone lifts it. */
  const [exemptUntil, setExemptUntil] = useState<Dayjs | null>(null);
  const closeExempt = () => { setExemptTarget(null); setExemptReason(''); setExemptUntil(null); };
  const [historyOf, setHistoryOf] = useState<any | null>(null);
  const [scheduleFor, setScheduleFor] = useState<any | null>(null);
  const [assignFor, setAssignFor] = useState<any | null>(null);
  const canAssign = (r: any) => (r.customerType === 'RETAILER' ? canEditRetailer : canEditFarmer);

  const exemptMut = useConfirmedMutation({ title: "Exempt this customer from visit coverage?", content: "They stop counting as overdue. Your reason is kept against the exemption.", okText: "Yes, exempt" }, {
    mutationFn: () => request({
      url: '/coverage/exemptions', method: 'POST',
      data: {
        customerType: exemptTarget.customerType, customerId: exemptTarget.customerId, reason: exemptReason,
        ...(exemptUntil ? { toDate: exemptUntil.format('YYYY-MM-DD') } : {}),
      },
    }),
    onSuccess: () => {
      message.success(exemptUntil
        ? `Exempted until ${formatIstDate(exemptUntil.format('YYYY-MM-DD'))} — it will stop appearing as overdue.`
        : 'Exempted — it will stop appearing as overdue.');
      closeExempt();
      qc.invalidateQueries({ queryKey: ['coverage-rows'] });
      qc.invalidateQueries({ queryKey: ['coverage-summary'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not exempt this customer.'),
  });

  /*
   * An exemption could be set but never lifted from the screen: the API had the route, the page had no
   * button, so a customer exempted "for the monsoon" stayed excused forever (V-08).
   */
  const liftMut = useConfirmedMutation<unknown, any, any>((r: any) => ({
    title: `Lift the exemption for ${r?.name ?? 'this customer'}?`,
    content: 'They count against their visit rule again from today, and show as due or overdue if a visit is owed.',
    okText: 'Yes, lift it',
  }), {
    mutationFn: (r: any) => request({ url: `/coverage/exemptions/${r.exemptionId}`, method: 'DELETE' }),
    onSuccess: (_: any, r: any) => {
      message.success(`Exemption lifted — ${r?.name ?? 'the customer'} counts against its rule again.`);
      qc.invalidateQueries({ queryKey: ['coverage-rows'] });
      qc.invalidateQueries({ queryKey: ['coverage-summary'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not lift the exemption.'),
  });

  // ── Columns ─────────────────────────────────────────────────────────────────────────────────
  const customerCols: DataTableColumn<any>[] = [
    {
      key: 'name', title: 'Customer', ellipsis: true, alwaysVisible: true, width: 250,
      render: (_: any, r: any) => {
        const u = r.state === 'COVERED' ? 'done' : urgencyOf(r.overdueDays, r.intervalDays);
        return (
          <div className="flex min-w-0 items-stretch gap-2">
            {/* One-glance urgency, the same scale the planner uses. */}
            <span className={`w-0.5 shrink-0 rounded-full ${URGENCY_BAR[u as Urgency]}`} />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 truncate font-medium">
                {audienceIcon(r.customerType)}
                <Link to={customerHref(r)} title={r.name}
                  className="truncate text-brand-700 hover:underline dark:text-brand-300">
                  {r.name}
                </Link>
              </div>
              <div className="type-caption truncate text-subtle" title={[r.code, r.place].filter(Boolean).join(' · ')}>
                {[r.code, r.place].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'customerType', title: 'Type', width: 96, align: 'center',
      render: (_: any, r: any) => <Pill color={r.customerType === 'RETAILER' ? 'blue' : 'lime'}>{r.customerType === 'RETAILER' ? 'Retailer' : 'Farmer'}</Pill>,
    },
    {
      /*
       * It declares a width for the sake of every OTHER column.
       *
       * This was the one column on the grain that left its width to the browser, and an auto-layout
       * table hands ALL its leftover pixels to whichever column will take them: with the reader's
       * saved layout down to three columns, Territory measured 1378px of a 1786px table — a screen
       * of white space that read as a broken page rather than as a wide column. Once every column
       * declares a width the table switches to a fixed layout and shares the slack out across all
       * of them (see `useFixed` in TwDataTable), so the space lands as breathing room everywhere
       * instead of as one dead field.
       */
      key: 'territoryName', title: 'Territory', ellipsis: true, width: 190,
      // Clicking a territory filters the whole register to it, rather than making the reader go
      // back up to the dropdowns and find the same name again.
      render: (_: any, r: any) => (r.territoryName
        ? (
          <button
            title={`Show only ${r.territoryName}`}
            onClick={(e) => { e.stopPropagation(); if (r.territoryId) drillTo(String(r.territoryId)); }}
            className="truncate text-brand-700 hover:underline dark:text-brand-300"
          >
            {r.territoryName}
          </button>
        )
        : <span className="text-subtle">not set</span>),
    },
    {
      key: 'repName', title: 'Assigned Staff', ellipsis: true, width: 200,
      /*
       * Name + role badge — a register mixing Sales Persons and Sales Heads is unreadable without
       * the role. The "Unassigned" capsule is a BUTTON: an unowned customer is the single most
       * actionable row on this screen, and making the reader go and find them in the People register
       * to fix it is the kind of detour that leaves customers unassigned for months.
       */
      render: (_: any, r: any) => (r.repName
        ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <UserLink id={r.repId} role={r.repRole} className="truncate">{r.repName}</UserLink>
            <RoleBadge role={r.repRole} />
            {canAssign(r) && (
              <Tooltip title="Reassign this customer">
                <button aria-label={`Reassign ${r.name}`} onClick={() => setAssignFor(r)}
                  className="shrink-0 rounded p-0.5 text-subtle opacity-0 hover:text-fg focus:opacity-100 group-hover:opacity-100">
                  <Pencil className="size-3.5" />
                </button>
              </Tooltip>
            )}
          </span>
        )
        : canAssign(r)
          ? (
            <Tooltip title="Assign a staff member">
              <button aria-label={`Assign staff to ${r.name}`} onClick={() => setAssignFor(r)}>
                <Pill color="orange">Unassigned →</Pill>
              </button>
            </Tooltip>
          )
          : <Pill color="orange">Unassigned</Pill>),
    },
    { key: 'normLabel', title: 'Coverage Rule', dataIndex: 'normLabel', align: 'center', width: 118, render: (v: any) => v ?? <span className="text-subtle">—</span> },
    {
      key: 'progress', title: pastWindow ? 'Past Window' : 'This Window', align: 'center', width: 132,
      render: (_: any, r: any) => {
        // The dates are printed under every figure, because the window is the rule's own cycle and
        // no two rows need share one — a monthly retailer and a quarterly farmer are both on screen.
        const span = windowSpan(r.cycleStart, r.cycleEnd);
        const dates = span ? <div className="type-caption text-subtle">{span}</div> : null;
        if (!r.policyId) {
          return (
            <Tooltip title={r.noNormReason ?? undefined}>
              <span className="inline-block"><span className="text-subtle">—</span>{dates}</span>
            </Tooltip>
          );
        }
        // The raw count is shown whenever the spacing rule discounted some of it, so a rep who made
        // five calls is never told they made two.
        const capped = r.visitsInCycle > r.done;
        return (
          <Tooltip title={capped ? `${r.visitsInCycle} visits in this window; ${r.done} count — the rest fell inside a slot already filled.` : undefined}>
            <span className="inline-block">
              <span className={r.done >= r.required ? 'font-semibold text-brand-600' : ''}>
                {r.done}/{r.required}{capped ? <span className="text-subtle"> (of {r.visitsInCycle})</span> : null}
              </span>
              {dates}
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'lastVisitAt', title: 'Last Visit', sortable: true, width: 116,
      render: (_: any, r: any) => (r.lastVisitAt ? formatIstDate(r.lastVisitAt) : <Pill color="red">never</Pill>),
    },
    {
      key: 'daysSinceLast', title: 'Days Since', dataIndex: 'daysSinceLast', align: 'right', sortable: true, width: 104,
      render: (v: any) => (v == null ? <span className="text-subtle">—</span> : `${v}d`),
    },
    { key: 'overdueDays', title: 'Next Due', sortable: true, align: 'right', width: 116, render: (_: any, r: any) => <OverdueCell days={r.overdueDays} /> },
    {
      key: 'state', title: 'Coverage Status', align: 'center', width: 152,
      render: (_: any, r: any) => {
        const s = COVERAGE_STATE[r.state] ?? COVERAGE_STATE.NO_NORM;
        // Two different subjects, so never `a ?? b`: an exemption says why this customer is excused,
        // a no-rule reason says why the window was not measured at all.
        // An exemption can sit on a customer no rule measures yet (set ahead of the rule's first window). The status
        // stays "No Visit Schedule", but the exemption is still said, so it can be found and lifted.
        const exemption = r.exemptionId
          ? `Exempt${r.exemptUntil ? ` until ${formatIstDate(r.exemptUntil)}` : ' until lifted'}: ${r.exemptReason ?? ''}`
          : null;
        const why = r.state === 'EXEMPT'
          ? exemption
          : r.state === 'NO_NORM' ? [r.noNormReason, exemption].filter(Boolean).join(' · ') || null : null;
        return <Tooltip title={why ?? undefined}><span><Pill color={s.color}>{s.label}</Pill></span></Tooltip>;
      },
    },
    {
      key: 'lastOrderAt', title: 'Last Order', sortable: true, width: 128,
      /* Three different facts, never collapsed into one: an order exists · this customer CAN order
         but has not · this customer cannot be linked to orders at all. Showing "₹0" for a farmer
         would assert they ordered nothing, which is a business fact we do not have. */
      render: (_: any, r: any) => (r.lastOrderAt ? formatIstDate(r.lastOrderAt)
        : r.salesLinked === false
          ? <Tooltip title="Orders are linked to retailers only, so a farmer has no sales history to show."><span className="text-subtle">No linked sales</span></Tooltip>
          : <span className="text-subtle">none</span>),
    },
    {
      key: 'lastOrderAmount', title: 'Last Order Value', align: 'right', sortable: true, width: 132,
      render: (_: any, r: any) => (r.lastOrderAmount
        ? <span className="font-medium">{inr(r.lastOrderAmount)}</span>
        : <span className="text-subtle">{r.salesLinked === false ? 'No linked sales' : '—'}</span>),
    },
    {
      key: 'actions', title: '', align: 'right', width: 136, alwaysVisible: true,
      render: (_: any, r: any) => (
        <div className="flex items-center justify-end gap-0.5">
          <RowAction tone="info" title="Visit history" label={`Visit history for ${r.name}`}
            icon={<History className="size-4" />} onClick={() => setHistoryOf(r)} />
          {canSchedule && (
            <RowAction tone="go" title="Schedule a visit" label={`Schedule a visit to ${r.name}`}
              icon={<CalendarPlus className="size-4" />} onClick={() => setScheduleFor(r)} />
          )}
          {canExempt && !r.exemptionId && r.state !== 'EXEMPT' && (
            <RowAction tone="stop" title="Exempt from its rule" label={`Exempt ${r.name}`}
              icon={<CircleSlash className="size-4" />} onClick={() => setExemptTarget(r)} />
          )}
          {canExempt && r.exemptionId && (
            <RowAction tone="go" title="Lift the exemption" label={`Lift the exemption for ${r.name}`}
              icon={<Undo2 className="size-4" />} onClick={() => liftMut.mutate(r)} />
          )}
          {/* View last, per the row-action convention. */}
          <RowAction tone="plain" to={customerHref(r)}
            title={r.customerType === 'RETAILER' ? 'Open customer' : 'Open the farmer register'}
            label={`Open ${r.name}`} icon={<ExternalLink className="size-4" />} />
        </div>
      ),
    },
  ];

  /*
   * A column heading that carries its own definition. Covered / Partly / Not Visited are the output
   * of a rule the reader never sees — they are not adjectives about the work, they are buckets of a
   * quota — so each one states the test that put a customer in it.
   *
   * The mark is `InfoTooltip`, the app's existing "there is an explanation here" affordance, rather
   * than a dotted underline: an underline is already spoken for by links, and a heading wearing one
   * invites a click that does nothing. Only the headings whose rule is not self-evident carry it, so
   * the mark still means something when you see it.
   */
  const StateHead = ({ label, hint }: { label: string; hint: string }) => (
    <span className="inline-flex items-center gap-1">
      {label}
      <InfoTooltip label={hint} ariaLabel={`What "${label}" counts`} size={13} />
    </span>
  );

  const territoryCols: DataTableColumn<any>[] = [
    {
      /*
       * NOT `ellipsis`, and it declares its own width.
       *
       * `ellipsis` puts the cell in a `max-w-0` box so a long STRING can be cut with an ellipsis.
       * This cell is not a string — it is a name and a level capsule — so in an auto-layout table
       * the box collapsed to nothing and the two pieces overlapped: the capsule sat on top of the
       * name, and "Ahmednagar" read as "Ahmedna…" behind a grey pill. The column is sized for a
       * district name plus its capsule instead, and the name WRAPS rather than hiding its own end,
       * because the name is the one thing this row is identified by.
       *
       * No drill chevron. Every row on this grain drills, so the arrow appeared on all of them and
       * marked nothing — it only sat between the name and its capsule looking like part of the
       * territory's identity. The caption beside the grain switch says rows are clickable and the
       * pointer cursor confirms it on the way to the click.
       */
      key: 'territoryName', title: 'Territory', alwaysVisible: true, width: 248,
      render: (_: any, r: any) => (
        <span className="flex items-center gap-1.5">
          <span className="min-w-0 break-words font-medium">{r.territoryName}</span>
          <TerritoryLevelTag level={r.territoryLevel} className="shrink-0" />
        </span>
      ),
    },
    { key: 'total', title: 'Customers', dataIndex: 'total', align: 'right', sortable: true, width: 122 },
    { key: 'covered', title: <StateHead label="Covered" hint="Every call the visit rule asks for in this window has been made — done is at or above required." />, dataIndex: 'covered', align: 'right', sortable: true, width: 112 },
    /*
     * PARTLY is the state a reader cannot guess, so the column says its rule outright rather than
     * leaving "why is this one partly?" to be worked out from four other columns. It is not a
     * judgement about visit QUALITY or a half-finished call: it is purely arithmetic on the rule's
     * quota — at least one call made, but fewer than the rule asks for, with the window still open.
     */
    { key: 'partial', title: <StateHead label="Partly" hint="Some of the rule's calls are done but not all of them — at least one visit, fewer than the quota. A rule asking for 2 calls a month with 1 made puts that customer here." />, dataIndex: 'partial', align: 'right', width: 100,
      render: (v: any, r: any) => (v > 0
        ? (
          <Tooltip label={`${v} of the ${r.total} customer(s) here have had at least one call this window but fewer than their rule asks for. The remaining calls are counted in Visits Pending.`}>
            <span className="font-medium text-warning-600 dark:text-harvest-300">{v}</span>
          </Tooltip>
        )
        : v) },
    /* An open window's "nothing counted yet" and a closed window's "nothing counted" are different
       facts with different counters; the page reads one window at a time, so the column shows its own. */
    pastWindow
      ? { key: 'missed', title: <StateHead label="Missed" hint="The window closed without one call that counted." />, dataIndex: 'missed', align: 'right', sortable: true, width: 128, render: (v: any) => (v > 0 ? <span className="font-semibold text-danger-600">{v}</span> : v) }
      : { key: 'notVisited', title: <StateHead label="Not Visited" hint="Not one call that counts yet in this window, which is still open." />, dataIndex: 'notVisited', align: 'right', sortable: true, width: 128, render: (v: any) => (v > 0 ? <span className="font-semibold text-danger-600">{v}</span> : (v ?? 0)) },
    { key: 'pendingVisits', title: <StateHead label="Visits Pending" hint="CALLS still owed here, not customers. Every customer's shortfall against its own rule, added up: two customers each one call short is 2." />, dataIndex: 'pendingVisits', align: 'right', sortable: true, width: 142 },
    { key: 'worstOverdue', title: <StateHead label="Worst Overdue" hint="The single longest wait in this territory: days past the allowed gap for whichever customer has gone longest without a call. A dash means nobody here is past their gap." />, dataIndex: 'worstOverdue', align: 'right', width: 148, render: (v: any) => (v > 0 ? `${v}d` : '—') },
    /*
     * NO RULE is a DATA-QUALITY bucket, not a pass and not a failure, and it is the one column on
     * this grain a reader cannot decode from its own name. No visit norm resolves for these
     * customers, usually because they sit in a territory nobody has set a rule on, so there is
     * nothing to be covered against: they are excluded from Covered % and Health rather than
     * counted as missed, which is why a territory made only of them reads as a dash.
     */
    { key: 'noNorm', title: <StateHead label="No Rule" hint="Customers here that NO visit rule covers, usually because no norm has been set on their territory. There is nothing to measure them against, so they are left out of Covered % and Health instead of counted as missed. A number above zero is a rule to go and set, not work that was skipped." />, dataIndex: 'noNorm', align: 'right', width: 104,
      render: (v: any, r: any) => (v > 0
        ? (
          <Tooltip label={`${v} of the ${r.total} customer(s) in ${r.territoryName} have no visit rule, so no call is owed for them and they sit outside the percentages on this row. Click to list them.`}>
            {/* The count OPENS the customers it counted. `stopPropagation`, because the row itself
                drills the whole territory and this button wants a narrower slice of it. */}
            <button
              type="button"
              aria-label={`List the ${v} customer(s) in ${r.territoryName} with no visit rule`}
              onClick={(e) => { e.stopPropagation(); if (r.territoryId) showNoRule(String(r.territoryId)); }}
              className="rounded font-medium text-brand-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:text-brand-400"
            >
              {v}
            </button>
          </Tooltip>
        )
        : v) },
    {
      key: 'coveragePct', title: <StateHead label="Covered %" hint="Covered, over the customers that a rule actually applies to — exempt customers and those with No Rule are left out of both sides, so the share describes one population." />, dataIndex: 'coveragePct', align: 'right', sortable: true, width: 150,
      render: (v: any) => (
        <CoverageBar pct={v} hint="Share of this territory's customers already covered in this window. Customers who are exempt, or whom no visit rule covers, are left out." />
      ),
    },
  ];

  const repCols: DataTableColumn<any>[] = [
    {
      // No ellipsis: two staff whose names differ only in their tail ("…SalesPerson" / "…SalesPerson2 (QA ONLY)")
      // became one row to the eye. The name wraps instead.
      key: 'repName', title: 'Sales Person / Head', alwaysVisible: true, width: 230,
      /*
       * The cell answers the COLUMN's question, which here is "whose book is this?" — so the answer
       * is that nobody owns it, not that the customers are unassigned. "Unassigned customers" sat in
       * the staff column describing the row's population, one cell away from a Customers count that
       * was already saying how many; read down the column it looked like a fourth rep.
       */
      render: (_: any, r: any) => (r.unassigned
        ? <Pill color="orange">No staff assigned</Pill>
        : (
          <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <UserLink id={r.repId} role={r.repRole} className="wrap-break-word">{r.repName}</UserLink>
            <RoleBadge role={r.repRole} />
          </span>
        )),
    },
    { key: 'total', title: 'Customers', dataIndex: 'total', align: 'right', sortable: true, width: 128 },
    { key: 'visitsMade30d', title: 'Visits Made (30d)', dataIndex: 'visitsMade30d', align: 'right', sortable: true, width: 158, render: (v: any) => <span className="font-semibold">{v}</span> },
    { key: 'pendingVisits', title: 'Visits Pending', dataIndex: 'pendingVisits', align: 'right', sortable: true, width: 142, render: (v: any) => (v > 0 ? <span className="font-semibold text-warning-600">{v}</span> : v) },
    { key: 'covered', title: 'Covered', dataIndex: 'covered', align: 'right', width: 112 },
    pastWindow
      ? { key: 'missed', title: 'Missed', dataIndex: 'missed', align: 'right', sortable: true, width: 128, render: (v: any) => (v > 0 ? <span className="text-danger-600">{v}</span> : v) }
      : { key: 'notVisited', title: 'Not Visited', dataIndex: 'notVisited', align: 'right', sortable: true, width: 128, render: (v: any) => (v > 0 ? <span className="text-danger-600">{v}</span> : (v ?? 0)) },
    { key: 'worstOverdue', title: 'Worst Overdue', dataIndex: 'worstOverdue', align: 'right', width: 148, render: (v: any) => (v > 0 ? `${v}d` : '—') },
    {
      key: 'coveragePct', title: 'Covered %', dataIndex: 'coveragePct', align: 'right', sortable: true, width: 150,
      render: (v: any) => (
        <CoverageBar pct={v} hint="Share of this person's customers already covered in this window. Exempt customers are left out." />
      ),
    },
    {
      key: 'drill', title: '', align: 'right', width: 48, alwaysVisible: true,
      render: (_: any, r: any) => (r.repId ? (
        <Tooltip title="See this person’s customers">
          <button aria-label={`See ${r.repName}'s customers`}
            onClick={() => { setRepId(String(r.repId)); setGrain('customer'); }}
            className="inline-flex size-8 items-center justify-center rounded-md text-subtle hover:bg-surface-3 hover:text-fg">
            <Search className="size-4" />
          </button>
        </Tooltip>
      ) : null),
    },
  ];

  /**
   * Nothing configured anywhere is a different problem from nothing matching a filter.
   *
   * Only when no narrowing of the list itself is on: with "No visit in 30 days" or a tile focus the table can be
   * empty for its own reason — a dealer visited this morning is simply not on the no-visit list — and telling
   * the reader to "set a rule" there points them at the wrong problem.
   */
  const nothingConfigured = !!summary && summary.total > 0 && summary.noNorm === summary.total && !noVisitDays && !focus;
  const filtersActive = !!(territoryId || audience || repId || q.trim() || noVisitDays || focus || windowBack);

  /*
   * "Visits made (30d)" counts the last thirty days from TODAY — a fact about the staff member, not
   * about the window being read. Beside covered/missed figures from an earlier cycle it would read as
   * that cycle's work, so it is dropped there rather than relabelled.
   */
  const repColsShown = pastWindow ? repCols.filter((c) => c.key !== 'visitsMade30d') : repCols;

  /*
   * The filter row, defined once and rendered once per breakpoint: inside the register's own
   * toolbar on a desktop, and above the cards on a phone. It used to sit in a row of its own on
   * both, which left the table's toolbar holding nothing but the column-settings gear — a ~56px
   * empty band across the page between the last filter and the first column heading.
   */
  const filterControls = (
    <>
      {terr.available && (
        <>
          {/* The cascade wears the LEVEL colours the territory chips already use — state
              blue, district cyan, beat green — so the control you set and the chip it puts
              on every row are one colour. The OPTIONS stay plain: a state list is 30 states,
              and one dot repeated 30 times marks nothing. */}
          <Select
            value={state ?? undefined} placeholder="All states" allowClear className="min-w-[140px] flex-1 sm:flex-none"
            style={filterTint(state ? TAG_TONE_HUE[TERRITORY_LEVEL.state.tone] : null)}
            onChange={(v: any) => { setState(v ?? null); setDistrict(null); setBeat(null); }}
            options={terr.optionsAt('state', null)}
          />
          <Select
            value={district ?? undefined} placeholder="All districts" allowClear className="min-w-[150px] flex-1 sm:flex-none"
            style={filterTint(district ? TAG_TONE_HUE[TERRITORY_LEVEL.district.tone] : null)}
            disabled={!state}
            onChange={(v: any) => { setDistrict(v ?? null); setBeat(null); }}
            options={state ? terr.optionsAt('district', state) : []}
          />
          <Select
            value={beat ?? undefined} placeholder="All beats" allowClear className="min-w-[140px] flex-1 sm:flex-none"
            style={filterTint(beat ? TAG_TONE_HUE[TERRITORY_LEVEL.beat.tone] : null)}
            disabled={!district}
            onChange={(v: any) => setBeat(v ?? null)}
            options={district ? terr.optionsAt('beat', district) : []}
          />
        </>
      )}
      {/* Audience carries the hue its Type pill wears in the table — retailers blue, farmers
          lime — so the filter and the rows it leaves are the same colour. */}
      <Select
        value={audience} placeholder="Retailers & farmers" allowClear className="min-w-[160px] flex-1 sm:flex-none"
        style={filterTint(audience ? (audience === 'RETAILER' ? TAG_TONE_HUE.blue : PALETTE.success[500]) : null)}
        onChange={(v: any) => setAudience(v)}
        options={[
          hueOption('RETAILER', 'Retailers only', TAG_TONE_HUE.blue),
          hueOption('FARMER', 'Farmers only', PALETTE.success[500]),
        ]}
      />
      {canViewUsers && (
        <Select
          value={repId} placeholder="All staff" allowClear showSearch
          optionFilterProp="name" optionLabelProp="name"
          className="min-w-[160px] flex-1 sm:flex-none"
          style={filterTint(repTint)}
          onChange={(v: any) => setRepId(v)}
          options={staffOptions}
        />
      )}
      {/* Which cycle to read. Next to the age filter because both ask "when", and before the
          search box because it changes what every figure on the page means. Every option
          carries its dates: the step alone ("2 windows back") is not a period a reader can
          check a number against. */}
      <Select
        value={windowBack ?? 0} optionLabelProp="selectedText" aria-label="Which window to read"
        className="min-w-[215px] flex-1 sm:flex-none"
        // Amber while a past window is being read — the same amber as the line above the
        // tiles, because they are saying the same thing about the same page.
        style={filterTint(pastWindow ? TAG_TONE_HUE.gold : null)}
        onChange={(v: any) => setWindowBack(Number(v) || undefined)}
        options={WINDOW_STEPS.map((n) => {
          const spans = spansFor(n);
          const dates = spansText(spans, !audience);
          // A step whose windows all closed before any rule existed is still worth opening —
          // "we had no rule then" is an answer — but it is said BEFORE the click, not left to
          // be guessed from a page of "No Visit Schedule".
          const noRuleYet = spans.length > 0 && spans.every((s) => s.beforeRule);
          return {
            value: n,
            label: (
              <span className="flex flex-col gap-0.5 leading-tight">
                <span>{stepName(n)}</span>
                {dates && <span className="type-caption text-subtle">{dates}</span>}
                {noRuleYet && <span className="type-caption text-warning-600 dark:text-harvest-300">No rule was in force yet</span>}
              </span>
            ),
            // The collapsed control names ONE span only when there is one. Picking the
            // biggest of several and appending "+1" would read as "the window is 3 Sep – 2
            // Dec", which is true of 1,737 customers and false of the rest; the count sends
            // the reader to the list, where both are named.
            selectedText: spans.length === 1
              ? `${stepName(n)} · ${windowSpan(spans[0].start, spans[0].end)}`
              : spans.length > 1 ? `${stepName(n)} · ${spans.length} ranges` : stepName(n),
            searchLabel: `${stepName(n)} ${dates}`.trim(),
            title: spans.map((s) => `${windowSpan(s.start, s.end)} — ${s.customers.toLocaleString('en-IN')} customer${s.customers === 1 ? '' : 's'}`).join('\n'),
          };
        })}
      />
      {/* Every option here selects the same thing — customers nobody has been to — so a dot
          per option would be one hue printed five times. The control takes the overdue red
          when it is set, which is where the colour is actually read. */}
      <Select
        value={noVisitDays} placeholder="Any last-visit age" allowClear className="min-w-[170px] flex-1 sm:flex-none"
        style={filterTint(noVisitDays ? TAG_TONE_HUE.red : null)}
        onChange={(v: any) => setNoVisitDays(v)}
        options={[30, 60, 90, 180, 365].map((d) => ({ value: d, label: `No visit in ${d} days` }))}
      />
      <Input
        value={q} onChange={(e: any) => setQ(e.target.value)} allowClear
        placeholder="Search name, code, place" aria-label="Search name, code, place" prefix={<Search className="size-4 text-subtle" />}
        className="min-w-[190px] flex-1 sm:flex-none sm:max-w-[240px]"
        style={filterTint(q.trim() ? PALETTE.sky[500] : null)}
      />
      {/* The way OUT of a narrowed page, in the bar that narrowed it. `clearFilters` already
          existed but only surfaced once the filters had emptied the table — by which point
          the reader has to work out which of eight controls to undo. It appears only when
          something is actually applied, so it never sits there disabled. `default`, not
          `ghost`: a ghost control beside filled inputs reads as decoration. */}
      {filtersActive && <FilterResetButton size="md" onClick={clearFilters} />}
    </>
  );

  const emptyForGrain = nothingConfigured ? (
    <EmptyState
      icon={<ClipboardList className="size-8" />}
      title="No Visit Schedule configured"
      description="Create a Visit Scheduler rule for this territory to start tracking customer coverage."
      /* The button, not a sentence about where the button lives. It carries the territory the reader
         is already looking at, so the rule form opens pointed at it. */
      action={canPlan ? (
        <Button variant="primary" size="sm" icon={<Plus className="size-4" />} onClick={() => goSetRule(territoryId)}>
          Set a rule{territoryId ? ` for ${terr.nameOf(territoryId) || 'this territory'}` : ''}
        </Button>
      ) : undefined}
    />
  ) : filtersActive ? (
    <EmptyState
      icon={<SearchX className="size-8" />}
      title="No customers found"
      description="No customers match the selected filters."
      action={<FilterResetButton onClick={clearFilters} />}
    />
  ) : (
    <EmptyState
      icon={<PartyPopper className="size-8" />}
      title="Excellent coverage"
      description="All assigned customers are covered."
    />
  );

  const covPct = summary?.coveragePct;

  return (
    <div className="space-y-4">
      {section === 'coverage' && (
        <>
          {/* Which cycle everything below is counting, and one click back to the live one. */}
          {pastWindow && (
            <div className="flex flex-wrap items-start gap-2 rounded-control border border-warning-500/25 bg-warning-500/10 px-3 py-2 type-caption text-warning-600 dark:text-harvest-300">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <strong>
                  Looking back {windowBack} window{windowBack === 1 ? '' : 's'}
                  {spansText(spansFor(windowBack ?? 0), !audience) ? ` — ${spansText(spansFor(windowBack ?? 0), !audience)}` : ''}.
                </strong>{' '}
                Every figure here — the tiles too — is as it stood on the last day of that window, and each
                row steps back by its own rule’s cycle, so the dates differ between a monthly and a quarterly
                rule. Each row prints its own under the figure in the <strong>Past Window</strong> column.
                Customers no rule reaches have no window of their own, so their last-visit dates are current.
              </span>
              <Button size="sm" icon={<CalendarClock className="size-4" />} onClick={() => setWindowBack(undefined)}>
                Back to this window
              </Button>
            </div>
          )}

          {/* KPI band — each tile is a server-side filter, and its hint says what it will do. */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            {/* Health leads: coverage % alone reads 0% for the first weeks of every cycle and tells
                a manager nothing. Health blends covered + in-progress + an overdue penalty. */}
            <StatCard
              label="Coverage Health" value={summary?.healthPct == null ? '—' : `${summary.healthPct}%`}
              tone={summary?.healthPct == null ? 'neutral' : summary.healthPct >= 70 ? 'success' : summary.healthPct >= 40 ? 'warning' : 'danger'}
              icon={<HeartPulse className="size-5" />}
              hint={summary ? `${summary.total} customers in scope` : 'in your scope'}
              onClick={filtersActive ? clearFilters : undefined} active={!filtersActive}
            />
            <StatCard
              label="Covered" value={covPct == null ? '—' : `${covPct}%`} tone="success" icon={<CheckCircle2 className="size-5" />}
              hint={summary ? `${summary.covered} of ${Math.max(0, summary.total - summary.noNorm - summary.exempt)} due` : undefined}
              onClick={() => setFocus(focus === 'COVERED' ? null : 'COVERED')} active={focus === 'COVERED'}
            />
            {/*
              The figure is the CUSTOMERS the tile opens — partly done, not visited yet and missed — so the
              count equals the rows below it. It used to print CALLS owed (100) over a list of 55 customers;
              the calls still owed are the sub-line, and the Visits Pending column carries them per row.
            */}
            <StatCard
              label="Customers Due" tone="warning" icon={<CalendarClock className="size-5" />}
              value={summary ? (summary.partial ?? 0) + (summary.notVisited ?? 0) + (summary.missed ?? 0) : '—'}
              hint={summary ? `${summary.pendingVisits} call${summary.pendingVisits === 1 ? '' : 's'} still owed` : 'calls still owed'}
              onClick={() => setFocus(focus === 'due' ? null : 'due')} active={focus === 'due'}
            />
            <StatCard
              label="Overdue" value={summary?.overdue ?? '—'} tone="danger" icon={<AlertTriangle className="size-5" />}
              hint="past their allowed gap"
              onClick={() => setFocus(focus === 'overdue' ? null : 'overdue')} active={focus === 'overdue'}
            />
            <StatCard
              label="No Visit Rule" value={summary?.noNorm ?? '—'} tone="neutral" icon={<CircleSlash className="size-5" />}
              hint="no rule covers these"
              onClick={() => setFocus(focus === 'NO_NORM' ? null : 'NO_NORM')} active={focus === 'NO_NORM'}
            />
          </div>

          <Card>
            {/* Where you are, and one click back out of it. */}
            <TerritoryTrail
              terr={terr} state={state} district={district} beat={beat}
              onPick={(level) => {
                if (level === 'all') { setState(null); setDistrict(null); setBeat(null); }
                if (level === 'state') { setDistrict(null); setBeat(null); }
                if (level === 'district') setBeat(null);
              }}
            />

            <div className="flex flex-wrap items-center gap-2">
              <Segmented
                value={grain}
                onChange={(v: any) => setGrain(String(v))}
                options={[
                  { value: 'customer', label: 'By customer' },
                  { value: 'territory', label: 'By territory' },
                  { value: 'rep', label: 'By staff' },
                ]}
              />
              {grain === 'territory' && <span className="type-caption text-subtle">Click a row to drill in.</span>}
              {/* The reader got here by asking "which customers have no rule?" — the next thing they
                  want is to set one, for the territory they are already narrowed to. */}
              {focus === 'NO_NORM' && canPlan && (
                <Button variant="primary" size="sm" icon={<Plus className="size-4" />} onClick={() => goSetRule(territoryId)}>
                  Set a rule{territoryId ? ` for ${terr.nameOf(territoryId) || 'this territory'}` : ''}
                </Button>
              )}
              {grain === 'rep' && pastWindow && (
                <span className="type-caption text-subtle">Visits made (30d) is hidden — it counts from today, not from this window.</span>
              )}
              {canExport && (
                <div className="ml-auto">
                  <Button size="sm" icon={<Download className="size-4" />} onClick={() => setExportOpen(true)}>Export</Button>
                </div>
              )}
            </div>

            {/* Phone only: with no table to hang them on, the filters keep their own row above
                the cards. On a desktop they live in the register's toolbar instead (below), so
                the column-settings gear shares that row rather than sitting alone on an empty
                strip between the filters and the first heading. */}
            <div className="mt-3 flex flex-wrap items-center gap-2 md:hidden">{filterControls}</div>

            {/* Phone: cards, so nothing is cropped or parked behind a horizontal scroll. */}
            <div className="mt-3 md:hidden">
              <MobileRows
                grain={grain} rows={rows ?? []} loading={isLoading} empty={emptyForGrain}
                canSchedule={canSchedule} canExempt={canExempt} pastWindow={pastWindow}
                onHistory={setHistoryOf} onSchedule={setScheduleFor} onExempt={setExemptTarget} onLift={(r: any) => liftMut.mutate(r)}
                onDrill={(id, level) => drillInto(id, level)}
                onRep={(id) => { setRepId(id); setGrain('customer'); }}
                onAssign={(r) => canAssign(r) && setAssignFor(r)}
              />
            </div>

            <div className="mt-3 hidden md:block">
              <DataTable
                toolbarLeft={filterControls}
                storageKey={`coverage-${grain}`}
                rowKey={(r: any) => (grain === 'customer' ? `${r.customerType}-${r.customerId}` : grain === 'territory' ? String(r.territoryId ?? 'none') : String(r.repId ?? 'none'))}
                columns={grain === 'customer' ? customerCols : grain === 'territory' ? territoryCols : repColsShown}
                dataSource={rows ?? []}
                loading={isLoading}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                onRow={grain === 'territory' ? (r: any) => ({
                  className: r.territoryId ? 'cursor-pointer' : undefined,
                  onClick: () => r.territoryId && drillInto(String(r.territoryId), r.territoryLevel),
                }) : undefined}
                emptyText={emptyForGrain}
              />
            </div>
          </Card>
        </>
      )}

      {section === 'schedule' && (
        <ScheduleTab
          staff={staff} canSchedule={canSchedule} territoryId={territoryId} me={me}
          /* The planner has no territory control of its own, so a cascade set on the Coverage tab
             used to narrow its due list invisibly — nothing on screen said so and nothing could
             clear it. It travels with its NAME now, and the tab shows it. */
          territory={territoryId
            ? { name: terr.nameOf(territoryId) || 'this territory', level: terr.nodes.get(String(territoryId))?.level }
            : null}
          onClearTerritory={() => { setState(null); setDistrict(null); setBeat(null); }}
        />
      )}

      {section === 'norms' && (
        <SchedulerTab
          canPlan={canPlan} territoryOptions={terr}
          presetTerritoryId={newRuleFor ?? null}
          onPresetUsed={() => setNewRuleFor(undefined)}
        />
      )}

      {/* ── Exempt ─────────────────────────────────────────────────────────── */}
      <Modal
        open={!!exemptTarget}
        onOpenChange={(o) => { if (!o) closeExempt(); }}
        title={`Exempt ${exemptTarget?.name ?? ''}`}
        footer={(
          <>
            <Button variant="default" onClick={closeExempt}>Cancel</Button>
            <Button variant="primary" disabled={!exemptReason.trim()} loading={exemptMut.isPending}
              onClick={() => exemptReason.trim() && exemptMut.mutate()}>Exempt</Button>
          </>
        )}
      >
        <p className="type-body text-muted">
          From today this customer stops counting as due or overdue — until the day you set below, or until
          the exemption is lifted. The reason is recorded against your name.
        </p>
        <label htmlFor="exempt-reason" className="type-label mt-3 block">Reason</label>
        <Input.TextArea
          id="exempt-reason"
          rows={3} value={exemptReason} onChange={(e: any) => setExemptReason(e.target.value)}
          placeholder="Why? e.g. shop closed for the season, dispute under review"
          className="mt-1"
        />
        <label htmlFor="exempt-until" className="type-label mt-3 block">Exempt until <span className="text-subtle font-normal">(optional)</span></label>
        <div className="mt-1">
          <DatePicker
            id="exempt-until"
            value={exemptUntil}
            onChange={(d: Dayjs | null) => setExemptUntil(d)}
            disabledDate={(d: Dayjs) => d.isBefore(dayjs(), 'day')}
            format="DD MMM YYYY"
            placeholder="Until lifted"
          />
        </div>
        <p className="type-caption text-subtle mt-1">
          {exemptUntil
            ? `They count against their rule again from ${formatIstDate(exemptUntil.add(1, 'day').format('YYYY-MM-DD'))}.`
            : 'Leave empty for an exemption that lasts until someone lifts it.'}
        </p>
      </Modal>

      <AssignStaffModal
        target={assignFor} staff={staff} terr={terr}
        onClose={() => setAssignFor(null)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['coverage-rows'] });
          qc.invalidateQueries({ queryKey: ['coverage-summary'] });
          qc.invalidateQueries({ queryKey: ['coverage-reps-picker'] });
        }}
      />
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export coverage"
        description={
          (noVisitDays
            ? `Customers with no visit in ${noVisitDays} days, with everything you have filtered to.`
            : grain === 'territory' ? 'Coverage rolled up by territory, with your filters applied.'
              : grain === 'rep' ? 'Coverage rolled up by staff — who owes how many visits, and to whom.'
                : 'One row per retailer and farmer, grouped by the staff member answerable for them.')
          + (pastWindow
            ? ` Counted over each customer’s window ${windowBack} back, as it stood the day that window closed.`
            : '')
        }
        baseUrl="/coverage/customers/export"
        baseParams={exportParams}
        formats={['xlsx', 'pdf', 'csv']}
        formatHint="Excel ships all three views as tabs · PDF is the printable register · CSV is the raw rows"
        /* No period picker: the window is not a date range chosen here, it is each rule's own cycle,
           and the page's window control has already chosen which one. The file follows the page. */
        hidePeriod
      />
      <CustomerHistoryModal target={historyOf} onClose={() => setHistoryOf(null)} />
      <ScheduleVisitModal
        target={scheduleFor} staff={staff} me={me}
        onClose={() => setScheduleFor(null)}
        onDone={() => { qc.invalidateQueries({ queryKey: ['coverage-schedule'] }); qc.invalidateQueries({ queryKey: ['coverage-due'] }); }}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * PHONE LAYOUT.
 *
 * A twelve-column register does not become usable on a 390px screen by scrolling sideways — the
 * columns that matter end up off-screen and stay there. Below `md` the same rows render as cards
 * with every field labelled, so nothing is cropped, truncated or hidden.
 *
 * Deliberately the SAME row objects and the same display language as the table: one source of
 * wording, so the phone and the desktop can never describe a customer differently.
 * ──────────────────────────────────────────────────────────────────────────── */
const PAGE_STEP = 20;

function KV({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="shrink-0 type-caption text-subtle">{label}</span>
      <span className="min-w-0 break-words text-right type-caption">{value}</span>
    </div>
  );
}

function MobileRows({
  grain, rows, loading, empty, canSchedule, canExempt, pastWindow, onHistory, onSchedule, onExempt, onLift, onDrill, onRep, onAssign,
}: {
  grain: string; rows: any[]; loading: boolean; empty: any;
  canSchedule: boolean; canExempt: boolean; pastWindow?: boolean;
  onHistory: (r: any) => void; onSchedule: (r: any) => void; onExempt: (r: any) => void; onLift?: (r: any) => void;
  onDrill: (id: string, level: string) => void; onRep: (id: string) => void;
  onAssign?: (r: any) => void;
}) {
  const [shown, setShown] = useState(PAGE_STEP);
  const visible = rows.slice(0, shown);

  if (loading) return <div className="p-8 text-center"><Spin /></div>;
  if (!rows.length) return <div className="rounded-card border border-line bg-surface shadow-e1 p-4">{empty}</div>;

  return (
    <div className="space-y-2">
      {visible.map((r) => {
        if (grain === 'customer') {
          const st = COVERAGE_STATE[r.state] ?? COVERAGE_STATE.NO_NORM;
          const u = r.state === 'COVERED' ? 'done' : urgencyOf(r.overdueDays, r.intervalDays);
          return (
            <article key={`${r.customerType}-${r.customerId}`} className="flex gap-2 rounded-card border border-line bg-surface shadow-e1 p-3">
              <span className={`w-1 shrink-0 rounded-full ${URGENCY_BAR[u as Urgency]}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 type-body font-semibold">
                      {audienceIcon(r.customerType)}
                      <Link to={customerHref(r)} className="break-words text-brand-700 hover:underline dark:text-brand-300">
                        {r.name}
                      </Link>
                    </div>
                    <div className="type-caption text-subtle">{[r.code, r.place].filter(Boolean).join(' · ')}</div>
                  </div>
                  <Pill color={st.color}>{st.label}</Pill>
                </div>

                <div className="mt-2 divide-y divide-line border-y border-line py-1">
                  <KV label="Type" value={r.customerType === 'RETAILER' ? 'Retailer' : 'Farmer'} />
                  <KV
                    label="Territory"
                    value={r.territoryName
                      ? <button onClick={() => r.territoryId && onDrill(String(r.territoryId), '')}
                          className="text-brand-700 hover:underline dark:text-brand-300">{r.territoryName}</button>
                      : 'not set'}
                  />
                  <KV
                    label="Assigned staff"
                    value={r.repName
                      ? <span className="inline-flex items-center gap-1.5">
                          <UserLink id={r.repId} role={r.repRole}>{r.repName}</UserLink>
                          <RoleBadge role={r.repRole} />
                        </span>
                      : onAssign
                        ? <button onClick={() => onAssign(r)}><Pill color="orange">Unassigned →</Pill></button>
                        : 'Unassigned'}
                  />
                  <KV label="Coverage rule" value={r.normLabel ?? '—'} />
                  <KV
                    label={pastWindow ? 'Past window' : 'This window'}
                    value={(() => {
                      const span = windowSpan(r.cycleStart, r.cycleEnd);
                      const fig = r.policyId
                        ? `${r.done}/${r.required}${r.visitsInCycle > r.done ? ` (of ${r.visitsInCycle})` : ''}`
                        : '—';
                      return span ? <span>{fig} <span className="text-subtle">· {span}</span></span> : fig;
                    })()}
                  />
                  <KV label="Last visit" value={r.lastVisitAt ? `${formatIstDate(r.lastVisitAt)}${r.daysSinceLast != null ? ` · ${r.daysSinceLast}d ago` : ''}` : 'never'} />
                  <KV label="Next due" value={<OverdueCell days={r.overdueDays} />} />
                  <KV label="Last order" value={r.lastOrderAt
                    ? `${formatIstDate(r.lastOrderAt)}${r.lastOrderAmount ? ` · ${inr(r.lastOrderAmount)}` : ''}`
                    : (r.salesLinked === false ? 'No linked sales' : 'none')} />
                  {r.exemptReason && <KV label="Exempt reason" value={r.exemptReason} />}
                  {r.exemptionId && <KV label="Exempt until" value={r.exemptUntil ? formatIstDate(r.exemptUntil) : 'until lifted'} />}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Button size="sm" icon={<History className="size-4" />} onClick={() => onHistory(r)}>History</Button>
                  {canSchedule && <Button size="sm" icon={<CalendarPlus className="size-4" />} onClick={() => onSchedule(r)}>Schedule</Button>}
                  {canExempt && !r.exemptionId && r.state !== 'EXEMPT' && (
                    <Button size="sm" icon={<CircleSlash className="size-4" />} onClick={() => onExempt(r)}>Exempt</Button>
                  )}
                  {canExempt && r.exemptionId && onLift && (
                    <Button size="sm" icon={<Undo2 className="size-4" />} onClick={() => onLift(r)}>Lift exemption</Button>
                  )}
                </div>
              </div>
            </article>
          );
        }

        if (grain === 'territory') {
          return (
            <article key={String(r.territoryId ?? 'none')}
              onClick={() => r.territoryId && onDrill(String(r.territoryId), String(r.territoryLevel))}
              className={`rounded-card border border-line bg-surface shadow-e1 p-3 ${r.territoryId ? 'cursor-pointer' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5 type-body font-semibold">
                  <span className="break-words">{r.territoryName}</span>
                  <TerritoryLevelTag level={r.territoryLevel} className="shrink-0" />
                </span>
                <CoverageBar pct={r.coveragePct} />
              </div>
              <div className="mt-2 divide-y divide-line border-t border-line pt-1">
                <KV label="Customers" value={r.total} />
                <KV label="Covered" value={r.covered} />
                <KV label="Partly covered" value={r.partial} />
                <KV label={pastWindow ? 'Missed' : 'Not visited'} value={(() => {
                  const n = Number((pastWindow ? r.missed : r.notVisited) ?? 0);
                  return n > 0 ? <span className="font-semibold text-danger-600">{n}</span> : n;
                })()} />
                <KV label="Visits pending" value={r.pendingVisits} />
                <KV label="Worst overdue" value={r.worstOverdue > 0 ? `${r.worstOverdue}d` : '—'} />
                <KV label="No rule" value={r.noNorm} />
              </div>
              {r.territoryId && <div className="mt-2 type-caption text-brand-700 dark:text-brand-300">Tap to drill in →</div>}
            </article>
          );
        }

        return (
          <article key={String(r.repId ?? 'none')} className="rounded-card border border-line bg-surface shadow-e1 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="flex min-w-0 items-center gap-1.5 type-body font-semibold">
                {r.unassigned
                  ? <Pill color="orange">No staff assigned</Pill>
                  : <><UserLink id={r.repId} role={r.repRole}>{r.repName}</UserLink><RoleBadge role={r.repRole} /></>}
              </span>
              <CoverageBar pct={r.coveragePct} />
            </div>
            <div className="mt-2 divide-y divide-line border-t border-line pt-1">
              <KV label="Customers" value={r.total} />
              {!pastWindow && <KV label="Visits made (30d)" value={<span className="font-semibold">{r.visitsMade30d}</span>} />}
              <KV label="Visits pending" value={r.pendingVisits > 0 ? <span className="font-semibold text-warning-600">{r.pendingVisits}</span> : r.pendingVisits} />
              <KV label="Covered" value={r.covered} />
              <KV label={pastWindow ? 'Missed' : 'Not visited'} value={(() => {
                const n = Number((pastWindow ? r.missed : r.notVisited) ?? 0);
                return n > 0 ? <span className="text-danger-600">{n}</span> : n;
              })()} />
              <KV label="Worst overdue" value={r.worstOverdue > 0 ? `${r.worstOverdue}d` : '—'} />
            </div>
            {r.repId && (
              <Button className="mt-2" size="sm" icon={<Search className="size-4" />} onClick={() => onRep(String(r.repId))}>
                See their customers
              </Button>
            )}
          </article>
        );
      })}

      {shown < rows.length && (
        <Button block onClick={() => setShown(shown + PAGE_STEP)}>
          Show {Math.min(PAGE_STEP, rows.length - shown)} more ({rows.length - shown} left)
        </Button>
      )}
    </div>
  );
}

/** Coverage percentage as a bar + figure — a number alone makes 40% and 90% look alike in a column. */
/**
 * A percentage is only readable once you know what it is a percentage OF.
 *
 * This column used to be headed "Coverage" and print a bare "0%" beside an empty track, on a page
 * whose first KPI tile is called "Coverage Health" — two different measures wearing one word. It is
 * headed "Covered %" now, next to the "Covered" COUNT it is derived from, and `hint` says in words
 * which customers are in the denominator. The words carry no figures on purpose: the count and the
 * total are already columns on this row, and a screen must not print the same number twice.
 */
function CoverageBar({ pct, hint }: { pct: number | null; hint?: string }) {
  if (pct == null) {
    return (
      <Tooltip title="Nobody here carries a visit rule, so there is nothing to cover yet.">
        <span className="text-subtle">—</span>
      </Tooltip>
    );
  }
  const tone = pct >= 80 ? 'bg-brand-500' : pct >= 40 ? 'bg-harvest-400' : 'bg-danger-500';
  return (
    <span className="flex items-center justify-end gap-2" title={hint}>
      <span className="hidden h-1.5 w-14 overflow-hidden rounded-full bg-surface-3 sm:block">
        <span className={`block h-full rounded-full ${tone}`} style={{ width: `${Math.max(2, pct)}%` }} />
      </span>
      <span className="font-medium tabular-nums">{pct}%</span>
    </span>
  );
}

/** State › District › Beat, with every level a way back up. Hidden until you have drilled in. */
function TerritoryTrail({ terr, state, district, beat, onPick }: {
  terr: ReturnType<typeof useStaffTerritories>;
  state: string | null; district: string | null; beat: string | null;
  onPick: (level: 'all' | 'state' | 'district') => void;
}) {
  if (!state && !district && !beat) return null;
  const crumb = (label: string, onClick?: () => void) => (
    <button onClick={onClick} disabled={!onClick}
      className={onClick ? 'text-muted hover:text-brand-700 hover:underline' : 'font-medium text-fg'}>
      {label}
    </button>
  );
  return (
    <nav aria-label="Territory" className="mb-3 flex flex-wrap items-center gap-1.5 type-caption">
      {crumb('All territories', () => onPick('all'))}
      {state && <><ChevronRight className="size-3 text-subtle" />{crumb(terr.nameOf(state) || 'State', district || beat ? () => onPick('state') : undefined)}</>}
      {district && <><ChevronRight className="size-3 text-subtle" />{crumb(terr.nameOf(district) || 'District', beat ? () => onPick('district') : undefined)}</>}
      {beat && <><ChevronRight className="size-3 text-subtle" />{crumb(terr.nameOf(beat) || 'Beat')}</>}
    </nav>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * SCHEDULE — the dated plan.
 *
 * Left: who is still owed a call, worst first. Right: the week, one column per day. Scheduling is a
 * single CLICK on a day chip rather than a drag: a drag needs a pointer, a steady hand and a target
 * big enough to hit, and this screen is used on a laptop trackpad and a tablet.
 * ──────────────────────────────────────────────────────────────────────────── */
function ScheduleTab({ staff, canSchedule, territoryId, territory, onClearTerritory, me }: {
  staff: any[]; canSchedule: boolean; territoryId?: string;
  /** The territory `territoryId` names, so the tab can SHOW the filter it is being narrowed by. */
  territory?: { name: string; level?: string } | null;
  onClearTerritory: () => void;
  me: any;
}) {
  const qc = useQueryClient();
  /*
   * Default to the signed-in person when they are field staff — a rep opening this screen wants their
   * own week. For anyone else it starts UNSET rather than on whoever sorts first in `/users`.
   */
  const isFieldStaff = (me?.roles ?? [me?.role]).some((r: string) => r === 'SALES_PERSON' || r === 'SALES_HEAD');
  const [userId, setUserId] = useUrlString<string | undefined>('schedUser', isFieldStaff && me?.id ? String(me.id) : undefined);

  const picked = useRef(false);
  const [weekStart, setWeekStart] = useUrlDay('week', dayjs().startOf('week').add(1, 'day'));  // Monday
  /*
   * WEEK plans, MONTH reviews.
   *
   * A week is the unit a rep actually walks, so that is where stops are added. A month is how a
   * manager checks the shape of the plan — is next week empty, is the 20th overloaded — and it
   * answers that as a calendar of counts, not as thirty scrollable columns.
   *
   * There is deliberately no YEAR view here: nobody assigns individual customers to days twelve
   * months out, and 300 day-cells would be a picture rather than a tool. Year-scale questions are
   * coverage questions ("did Nagpur get worked this quarter?") and the Coverage tab already answers
   * them against the visit rules.
   */
  const [view, setView] = useUrlTab('plan', ['week', 'month'] as const, 'week');
  const isMonth = view === 'month';

  /*
   * A notification about a stop links here with `date=` — the day of the stop. The planner read only `week`, so
   * "Rohit has put Wagh on your plan for 21 Sep" opened on 14 – 19 Sep, where the stop is not (V-03). The date is
   * turned into its week once, on arrival, and dropped from the address so it cannot fight the stepper.
   */
  const [dateParam, setDateParam] = useUrlParam('date');
  useEffect(() => {
    if (!dateParam) return;
    const d = dayjs(dateParam);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateParam) && d.isValid()) {
      // Sunday is not a planning day; its stops (if any) belong to the week that just ended.
      setWeekStart(d.day() === 0 ? d.subtract(6, 'day') : d.startOf('week').add(1, 'day'));
      setView('week');
    }
    setDateParam(undefined);
  }, [dateParam]);

  const days = useMemo(() => Array.from({ length: 6 }, (_, i) => weekStart.add(i, 'day')), [weekStart]); // Mon–Sat

  /*
   * Which month a week belongs to, when it straddles two.
   *
   * The week of Mon 31 Aug is, to anyone reading it, the first week of SEPTEMBER — four of its six
   * working days are in September. Anchoring on the Monday put the month view on August and made
   * switching from Week to Month look like it had jumped backwards. The Thursday is the week's
   * centre of gravity, so it names the month the way a person would.
   */
  const monthAnchor = weekStart.add(3, 'day');
  /*
   * "07 – 12 Sep" / "28 Sep – 03 Oct" — the stepper's value is a WEEK, so it prints the week.
   *
   * It used to print "07 Sep", the first day, which is not the thing being stepped: a reader
   * checking whether Saturday is in view had to open the calendar to find out. The month is printed
   * once when both ends share it, and on both ends when the week straddles two.
   */
  const weekSpan = useMemo(() => {
    const end = weekStart.add(5, 'day');
    return `${weekStart.format(end.isSame(weekStart, 'month') ? 'DD' : 'DD MMM')} – ${end.format('DD MMM')}`;
  }, [weekStart]);

  const from = (isMonth ? monthAnchor.startOf('month') : weekStart).format('YYYY-MM-DD');
  const to = (isMonth ? monthAnchor.endOf('month') : weekStart.add(5, 'day')).format('YYYY-MM-DD');

  /** The month as calendar rows, Monday-first, padded so every row has 7 cells. */
  const monthGrid = useMemo(() => {
    const first = monthAnchor.startOf('month');
    const lead = (first.day() + 6) % 7;            // Monday = 0
    const total = monthAnchor.daysInMonth();
    const cells: Array<Dayjs | null> = [
      ...Array.from({ length: lead }, () => null),
      ...Array.from({ length: total }, (_, i) => first.add(i, 'day')),
    ];
    while (cells.length % 7) cells.push(null);
    return Array.from({ length: cells.length / 7 }, (_, r) => cells.slice(r * 7, r * 7 + 7));
  }, [weekStart]);
  const today = dayjs().format('YYYY-MM-DD');

  /*
   * Pending work per rep, so the picker says WHO NEEDS PLANNING before you choose one.
   *
   * Reuses `/coverage/reps` — the same roll-up the Coverage tab's "By staff" grain renders, so the
   * number in this dropdown can never disagree with the number on that table. Scoped server-side,
   * so a Sales Head sees their own team here and nobody else's.
   */
  const { data: repStats } = useQuery({
    queryKey: ['coverage-reps-picker'],
    queryFn: () => request<any[]>({ url: '/coverage/reps' }),
    staleTime: 60_000,
  });
  const statsByRep = useMemo(() => {
    const m = new Map<string, { pending: number; overdue: number }>();
    for (const r of repStats ?? []) {
      if (r.repId) m.set(String(r.repId), { pending: Number(r.pendingVisits ?? 0), overdue: Number(r.worstOverdue ?? 0) > 0 ? Number(r.overdue ?? 0) : 0 });
    }
    return m;
  }, [repStats]);

  /**
   * Staff, heaviest workload first.
   *
   * Alphabetical put whoever happens to be called "Asmita" at the top; a planner opens this screen
   * to deal with the person who is furthest behind. Search covers the case where you want someone
   * specific, so ordering can be spent on the more common need.
   */
  const staffOptions = useMemo(() => {
    /*
     * A rep cannot read the staff list (no USER.VIEW), so their own id was the picker's value with no option
     * behind it, and the closed control printed "231" (V-10). The signed-in person is always an option of
     * their own planner.
     */
    const list = [...staff];
    if (me?.id && !list.some((u) => String(u.id) === String(me.id)) && isFieldStaff) {
      list.push({ id: me.id, fullName: me.fullName ?? me.name ?? 'Me', roleCode: me.role });
    }
    /*
     * The same gap one level up: a Sales Head without USER.VIEW has no staff list either, so opening "A planned visit was
     * called off" put "14" in the picker. The coverage roll-up already names every rep in the Head's scope, so those names
     * fill in whoever the staff list could not.
     */
    for (const r of repStats ?? []) {
      if (r.repId && r.repName && !list.some((u) => String(u.id) === String(r.repId))) {
        list.push({ id: r.repId, fullName: r.repName, roleCode: r.repRole });
      }
    }
    return list
      .map((u) => {
        const s = statsByRep.get(String(u.id));
        return {
          value: String(u.id),
          name: String(u.fullName ?? ''),
          label: String(u.fullName ?? ''),
          roles: rolesOfUser(u),
          pending: s?.pending ?? 0,
          overdue: s?.overdue ?? 0,
        };
      })
      .sort((a, b) => b.overdue - a.overdue || b.pending - a.pending || a.name.localeCompare(b.name));
  }, [staff, statsByRep, repStats, me, isFieldStaff]);

  /*
   * Never open on an empty screen.
   *
   * A rep lands on their own week. A manager used to land on "Pick a staff member" and a blank card
   * — a planner that shows nothing until you have already made a decision teaches you the tab is
   * broken. They now land on the rep with the most outstanding work, and can switch.
   *
   * `picked` guards it so this only ever fills an EMPTY selection: once someone chooses a rep, a
   * late-arriving staff list must not yank them somewhere else.
   *
   * Declared AFTER `staffOptions` on purpose — it reads that const, and reading it above its
   * declaration is a temporal-dead-zone crash on first render, not a lint nit.
   */
  useEffect(() => {
    if (picked.current || userId || !staffOptions.length) return;
    setUserId(staffOptions[0].value);
  }, [staffOptions, userId]);

  /**
   * Where the period on screen sits relative to today, in words — "31 Aug – 05 Sep" alone never says,
   * and neither does "Oct 2026". Empty for the CURRENT period: the stepper already prints which one
   * it is, and the "This week" / "This month" button beside it is disabled, which says the rest.
   */
  const periodLabel = useMemo(() => {
    const unit = isMonth ? 'month' : 'week';
    const home = dayjs().startOf('week').add(1, 'day');
    // Whether this IS today's period is an identity test, not a subtraction: `diff(…, 'week')`
    // truncates, so a Tuesday-anchored week six days behind today's Monday measured as 0 — "the
    // current week" — and the way home disappeared on a week that was not the current one.
    const atHome = isMonth ? monthAnchor.isSame(dayjs(), 'month') : weekStart.isSame(home, 'day');
    if (atHome) return '';
    const diff = isMonth
      ? monthAnchor.startOf('month').diff(dayjs().startOf('month'), 'month')
      : Math.round(weekStart.startOf('day').diff(home, 'day') / 7);
    if (diff === 1) return `next ${unit}`;
    if (diff === -1 || diff === 0) return `last ${unit}`;
    return diff > 0 ? `in ${diff} ${unit}s` : `${-diff} ${unit}s ago`;
  }, [isMonth, weekStart, monthAnchor]);

  const { data: due, isLoading: dueLoading } = useQuery({
    queryKey: ['coverage-due', userId, territoryId, from, to],
    // Every customer owed a call, no cap. It was cut at 60, then at 200 — and a rep's book runs past both (Test - SP is owed
    // 161 today); a list silently cut reports a smaller problem than the one on the ground, and its count pill with it.
    queryFn: () => request<any[]>({ url: '/coverage/schedule/due', params: { ...(userId ? { userId } : {}), ...(territoryId ? { territoryId } : {}), from, to, limit: String(UNCAPPED) } }),
    enabled: !!userId,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  /*
   * The plan is shared: the rep marks stops done in the field while their Head has this week open. It was read
   * once, so 34 seconds after a rep cancelled a trip the Head's planner still said "In Progress" (V-23). It now
   * re-reads every 30 seconds while open, and whenever the tab comes back into focus.
   */
  const { data: stops, isLoading: stopsLoading } = useQuery({
    queryKey: ['coverage-schedule', userId, from, to],
    queryFn: () => request<any[]>({ url: '/coverage/schedule', params: { userId: userId!, from, to } }),
    enabled: !!userId,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  /*
   * "Today" is today, whichever period is on screen. It was read out of the loaded week, so on any other week it
   * said "Nothing planned for today" beside a rep with stops today (V-12). When today is outside the period, its
   * stops are asked for on their own.
   */
  const todayInView = today >= from && today <= to;
  const { data: todayStops } = useQuery({
    queryKey: ['coverage-schedule', userId, today, today],
    queryFn: () => request<any[]>({ url: '/coverage/schedule', params: { userId: userId!, from: today, to: today } }),
    enabled: !!userId && !todayInView,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['coverage-schedule'] });
    qc.invalidateQueries({ queryKey: ['coverage-due'] });
  };

  const addMut = useConfirmedMutation({ title: "Add this customer to the visit plan?", okText: "Yes, add" }, {
    mutationFn: (v: { planDate: string; customerType: string; customerId: string }) =>
      request({ url: '/coverage/schedule', method: 'POST', data: { userId, ...v } }),
    onSuccess: invalidate,
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not add that stop.'),
  });
  const delMut = useConfirmedMutation({ title: "Remove this visit from the plan?", content: "The day is freed up and the customer goes back to being counted as due.", okText: "Yes, remove", danger: true }, {
    mutationFn: (id: string) => request<any>({ url: `/coverage/schedule/${id}`, method: 'DELETE' }),
    onSuccess: (r: any) => {
      // A stop in the past is CANCELLED, not deleted — erasing it would erase the fact that a
      // commitment was made and not kept.
      if (r?.cancelled) message.info('Marked as cancelled. Past visits stay on the record.');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not remove that stop.'),
  });
  const genMut = useConfirmedMutation({ title: "Generate a visit schedule for this period?", content: "Planned visits are created across the date range from the coverage rules.", okText: "Yes, generate" }, {
    mutationFn: () => request<any>({ url: '/coverage/schedule/generate', method: 'POST', data: { userId, from, to, perDayCap: 8 } }),
    onSuccess: (r: any) => {
      message.success(r?.created ? `Planned ${r.created} visit${r.created === 1 ? '' : 's'} across the week.` : 'Nothing left to plan for this week.');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not auto-plan.'),
  });

  const byDay = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const s of stops ?? []) (m[s.planDate] ??= []).push(s);
    return m;
  }, [stops]);

  /*
   * The loaded window's stops keyed by customer, so a due row can say what is already arranged for
   * it without a second request — and reads the SAME rows the day columns render, so the panel and
   * the calendar beside it cannot tell two different stories about one customer.
   */
  const stopsByCustomer = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const s of stops ?? []) (m[`${s.customerType}|${s.customerId}`] ??= []).push(s);
    return m;
  }, [stops]);

  const todaysStops = (todayInView ? byDay[today] : todayStops) ?? [];
  const doneToday = todaysStops.filter((s) => s.status === 'COMPLETED').length;
  const overdueCount = (due ?? []).filter((d) => (d.overdueDays ?? -1) > 0).length;
  /*
   * The picker never prints a raw id. Until the staff lists arrive (a notification opens this tab cold), the chosen person
   * is named from their own plan rows, which carry `repName` and load first; failing that, the field says it is loading.
   */
  const pickerOptions = useMemo(() => {
    if (!userId || staffOptions.some((o) => o.value === String(userId))) return staffOptions;
    const named = [...(stops ?? []), ...(todayStops ?? [])].find((s: any) => String(s.userId) === String(userId))?.repName;
    return [...staffOptions, { value: String(userId), name: named ?? 'Loading…', label: named ?? 'Loading…', roles: [], pending: 0, overdue: 0 }];
  }, [staffOptions, userId, stops, todayStops]);
  const repName = pickerOptions.find((o) => o.value === String(userId))?.name || 'this staff member';
  // Plans not kept this week. A different fact from "owed a visit", and the panel has to be able to
  // say so when it is empty and the calendar beside it is red.
  const missedThisWeek = (stops ?? []).filter((s: any) => s.status === 'MISSED').length;

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        {/* The picker carries each rep's OUTSTANDING WORK, so you can see who needs planning without
            selecting them one by one. `optionLabelProp="name"` keeps the closed control clean —
            the badge belongs in the list, not in the field. Search matches the name only. */}
        <Select
          value={userId} placeholder="Pick a staff member" showSearch
          optionFilterProp="name" optionLabelProp="name"
          className="min-w-[220px] flex-1 sm:flex-none"
          onChange={(v: any) => { picked.current = true; setUserId(v); }}
          options={pickerOptions}
          optionRender={(o: any) => {
            /*
             * The Select puts its ✓ AFTER this content and only on the selected row, so that row's
             * content box is 24px narrower (a size-4 tick plus the row's gap-2). Left unhandled, the
             * badge on the selected option sits left of every other badge and the column reads
             * ragged. Reserving the same 24px on the unselected rows lines them all up.
             */
            const isSel = String(o.value) === String(userId);
            return (
              <span className="flex w-full items-center gap-2">
                <span className="min-w-0 flex-1 truncate">{o.name}</span>
                {/* What they ARE, then what they OWE — a Head and a rep in one list are not the
                    same row, and the workload pill cannot say which is which. */}
                {(o.roles ?? []).map((c: string) => <RoleBadge key={c} role={c} />)}
                <span className="shrink-0">
                  {o.overdue > 0
                    ? <Pill color="red">{o.overdue} overdue</Pill>
                    : o.pending > 0
                      ? <Pill color="gold">{o.pending} pending</Pill>
                      : <Pill color="green">covered</Pill>}
                </span>
                {!isSel && <span aria-hidden className="w-4 shrink-0" />}
              </span>
            );
          }}
        />
        {/* The filter this tab is being narrowed by, carried over from the Coverage tab — visible,
            and clearable, because an invisible filter is indistinguishable from a broken screen. */}
        {territory && (
          <Tooltip title={`The list of customers still owed a visit is narrowed to ${territory.name} — click to clear it`}>
            <button type="button" onClick={onClearTerritory} aria-label={`Clear the ${territory.name} filter`}>
              <Pill color={TERRITORY_LEVEL[String(territory.level ?? '').toLowerCase()]?.tone ?? 'default'}>
                {TERRITORY_LEVEL[String(territory.level ?? '').toLowerCase()]?.icon}
                {territory.name}
                <X className="size-3" />
              </Pill>
            </button>
          </Tooltip>
        )}
        {canSchedule && (
          <Button variant="primary" size="sm" icon={<Wand2 className="size-4" />} loading={genMut.isPending}
            disabled={!userId || isMonth} onClick={() => genMut.mutate()} className="ml-auto">
            Auto-plan this week
          </Button>
        )}
      </div>

      {/*
        Navigation on its OWN row. It used to share a line with the staff picker and the auto-plan
        button, so the next arrow sat on top of the date caption at anything under a wide desktop.
        The range is a DATE PICKER (pick any day, land on its period) and it prints the whole period,
        not its first day. The two labels beside it appear only when they SAY something: on the
        period you are already in, "this week" and a dead "This week" button are a caption and a
        control that can do nothing, so both step aside and the toolbar is just the stepper.
      */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Segmented
          value={view}
          onChange={(v: any) => {
            // Coming back to the week grid, land on a MONDAY. `weekStart` may be sitting on whatever
            // day the month view anchored to — "This month" parks it on the 1st — and the grid reads
            // six days forward from it, so a month starting on a Tuesday opened a Tue–Sun "week".
            if (String(v) === 'week') setWeekStart(weekStart.startOf('week').add(1, 'day'));
            setView(String(v));
          }}
          options={[{ value: 'week', label: 'Week' }, { value: 'month', label: 'Month' }]}
        />
        <div className="flex flex-wrap items-center gap-1.5">
          <Tooltip title={isMonth ? 'Previous month' : 'Previous week'}>
            <span>
              <Button size="sm" aria-label={isMonth ? 'Previous month' : 'Previous week'} icon={<ChevronLeft className="size-4" />}
                onClick={() => setWeekStart(isMonth ? monthAnchor.subtract(1, 'month').startOf('month') : weekStart.subtract(7, 'day'))} />
            </span>
          </Tooltip>

          <Tooltip title={isMonth ? 'Jump to any month' : 'Jump to any week'}>
            <span>
              <DatePicker
                value={isMonth ? monthAnchor : weekStart}
                onChange={(d: any) => d && setWeekStart(isMonth ? d.startOf('month').add(3, 'day').startOf('week').add(1, 'day') : d.startOf('week').add(1, 'day'))}
                format={isMonth ? 'MMM YYYY' : 'DD MMM'}
                displayValue={isMonth ? undefined : weekSpan}
                allowClear={false}
                // Sized to the value, not to a guess. A fixed width (and the control's own 9rem floor)
                // left dead space between "31 Aug" and the next arrow, so the stepper read as broken
                // apart; `fmtCh` still reserves enough room for the longest value the format renders.
                // The empty placeholder is what keeps that reservation honest: the field can never be
                // empty (allowClear is off), so widening it to fit the words "Select date" — which
                // will never print — was most of the gap.
                placeholder=""
                className="w-auto [&_button]:min-w-0"
              />
            </span>
          </Tooltip>

          <Tooltip title={isMonth ? 'Next month' : 'Next week'}>
            <span>
              <Button size="sm" aria-label={isMonth ? 'Next month' : 'Next week'} icon={<ChevronRight className="size-4" />}
                onClick={() => setWeekStart(isMonth ? monthAnchor.add(1, 'month').startOf('month') : weekStart.add(7, 'day'))} />
            </span>
          </Tooltip>
        </div>

        {/* Only where the period SITS. The dates themselves are on the stepper now, and printing
            them twice on one line ("Oct 2026 · October 2026") makes a reader check one against the
            other. On the current period it says nothing at all, because the disabled "This week"
            button beside it is already that message — and "this week · This week" reads as a bug. */}
        {!!periodLabel && (
          <span className="type-caption whitespace-nowrap text-subtle">{periodLabel}</span>
        )}

        {/* The way home, and only when there is somewhere to come home FROM — it renders on exactly
            the periods `periodLabel` speaks on, so the words and the button arrive together. */}
        {!!periodLabel && (
          <Button size="sm"
            onClick={() => setWeekStart(isMonth ? dayjs().startOf('month') : dayjs().startOf('week').add(1, 'day'))}>
            {isMonth ? 'This month' : 'This week'}
          </Button>
        )}
      </div>

      {!userId ? (
        <EmptyState icon={<Users className="size-8" />} title="Pick a staff member" description="Choose whose week you want to plan." />
      ) : (
        <>
          {/* "What should I visit today?" — answered before anything else on the screen. */}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-line bg-surface-2 px-3 py-2">
            <span className="type-label">Today</span>
            <span className="type-body">
              {todaysStops.length
                ? <><strong>{todaysStops.length}</strong> visit{todaysStops.length === 1 ? '' : 's'} planned
                    {doneToday ? <> · <span className="text-brand-600">{doneToday} done</span></> : null}</>
                : <span className="text-subtle">Nothing planned for today</span>}
            </span>
            {overdueCount > 0 && (
              <span className="type-body text-danger-600">
                <strong>{overdueCount}</strong> customer{overdueCount === 1 ? '' : 's'} overdue
              </span>
            )}
            <span className="ml-auto flex flex-wrap items-center gap-2 type-caption text-subtle">
              {/* Red carries two different facts on this screen — a customer past their allowed gap
                  in the list, a plan not kept on the calendar — so the key says both. */}
              <LegendDot cls={URGENCY_BAR.overdue} label="Missed or overdue" />
              <LegendDot cls={URGENCY_BAR.soon} label="Due soon" />
              <LegendDot cls={URGENCY_BAR.planned} label="Planned" />
              <LegendDot cls={URGENCY_BAR.done} label="Completed" />
            </span>
          </div>

          <div className="mt-3 grid gap-4 lg:grid-cols-[320px_1fr]">
            {/* ── Who is owed a call ───────────────────────────────────────── */}
            <div className="rounded-lg border border-line">
              <div className="border-b border-line px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="type-label">Still owed a visit</span>
                  {!dueLoading && !!due?.length && <Pill color="red">{due.length}</Pill>}
                </div>
                <div className="type-caption text-subtle">
                  Worst first. Click a day to plan it.
                  {/* The panel states its own scope, because it is NOT the scope of the plan beside
                      it: the calendar shows every stop on this rep's week wherever it is. */}
                  {territory ? <> Only {territory.name}.</> : null}
                </div>
              </div>
              <div className="max-h-[560px] overflow-y-auto">
                {dueLoading ? (
                  <div className="p-6 text-center"><Spin /></div>
                ) : !due?.length ? (
                  /*
                   * An empty list is not a verdict on the whole business — it is the answer for ONE
                   * rep, in whatever territory this tab has been narrowed to. "Excellent coverage /
                   * all assigned customers are covered" was printed beside a week of missed stops,
                   * because the list was silently narrowed to a beat this rep has no customers in.
                   */
                  territory ? (
                    <EmptyState compact icon={<SearchX className="size-7" />}
                      title={`Nothing owed in ${territory.name}`}
                      description={`This list is narrowed to ${territory.name}, carried over from the Coverage tab. The plan on the right is not — it shows every stop on ${repName}’s week, wherever it is.`}
                      action={<Button size="sm" onClick={onClearTerritory}>Show the whole book</Button>} />
                  ) : (
                    <EmptyState compact icon={<PartyPopper className="size-7" />}
                      title="Nothing owed right now"
                      description={`Every customer on ${repName}’s book has met its visit rule for the window it is in.`} />
                  )
                ) : due.map((d) => {
                  const u = urgencyOf(d.overdueDays, d.intervalDays);
                  return (
                    <div key={`${d.customerType}-${d.customerId}`} className="flex gap-2 border-b border-line px-3 py-2 last:border-0">
                      <span className={`w-0.5 shrink-0 rounded-full ${URGENCY_BAR[u]}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {audienceIcon(d.customerType)}
                          <Link to={customerHref(d)} title={d.name}
                            className="truncate type-body font-medium text-brand-700 hover:underline dark:text-brand-300">
                            {d.name}
                          </Link>
                        </div>
                        <div className="type-caption text-subtle">
                          {d.territoryName ?? 'no territory'} · {d.done}/{d.required} done · <OverdueCell days={d.overdueDays} />
                        </div>
                        {d.lastOrderAmount && (
                          <div className="type-caption text-subtle">
                            <ShoppingCart className="mr-1 inline size-3" />last order {inr(d.lastOrderAmount)}
                          </div>
                        )}
                        {scheduleNotes(stopsByCustomer[`${d.customerType}|${d.customerId}`] ?? []).map((n) => (
                          <div key={n.key} className={`type-caption ${n.tone}`}>
                            <n.Icon className="mr-1 inline size-3" />{n.text}
                          </div>
                        ))}
                        {canSchedule && (
                          <div className="mt-1.5 flex gap-1">
                            {days.map((day) => {
                              const iso = day.format('YYYY-MM-DD');
                              const already = d.plannedOn.includes(iso);
                              const past = iso < today;
                              return (
                                <Tooltip
                                  key={iso}
                                  title={past ? `${day.format('ddd DD MMM')} has already passed`
                                    : already ? `Already planned for ${day.format('ddd DD MMM')}`
                                    : `Plan for ${day.format('ddd DD MMM')}`}
                                >
                                  <button
                                    aria-label={`Plan ${d.name} for ${day.format('dddd DD MMM')}`}
                                    disabled={already || past || addMut.isPending}
                                    onClick={() => addMut.mutate({ planDate: iso, customerType: d.customerType, customerId: d.customerId })}
                                    className={`size-7 rounded-md border type-caption font-medium transition
                                      ${already
                                        ? 'cursor-default border-brand-500/40 bg-brand-500/12 text-brand-700 dark:text-brand-300'
                                        : past
                                          ? 'cursor-not-allowed border-line/60 text-subtle/50'
                                          : 'border-line text-muted hover:border-brand-500 hover:bg-brand-500/10 hover:text-brand-700'}`}
                                  >
                                    {day.format('dd')[0]}
                                  </button>
                                </Tooltip>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* A missed plan and an owed call are different facts; when the panel is empty and
                    the week is full of red, say which is which rather than leaving the two to be
                    read as a contradiction. */}
                {!dueLoading && !due?.length && missedThisWeek > 0 && (
                  <div className="border-t border-line bg-warning-500/10 px-3 py-2 type-caption text-warning-600 dark:text-harvest-300">
                    <TriangleAlert className="mr-1 inline size-3.5" />
                    {missedThisWeek} planned visit{missedThisWeek === 1 ? ' this week was' : 's this week were'} not made.
                    {territory
                      ? ` Those customers are not in this list because it only covers ${territory.name}.`
                      : ' Those customers are still not owed a call: their rule is already met for this window, they are exempt, or no rule covers them.'}
                  </div>
                )}
                {!dueLoading && due?.length === 200 && (
                  <div className="border-t border-line px-3 py-2 type-caption text-subtle">
                    Showing the 200 most urgent — there are more.
                  </div>
                )}
              </div>
            </div>

            {/* ── The month, at a glance ───────────────────────────────────── */}
            {isMonth ? (
              <div className="rounded-lg border border-line p-2">
                <div className="mb-1 grid grid-cols-7 gap-1">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                    <div key={d} className="text-center type-caption text-subtle">{d}</div>
                  ))}
                </div>
                {monthGrid.map((row, ri) => (
                  <div key={ri} className="mb-1 grid grid-cols-7 gap-1">
                    {row.map((day, ci) => {
                      if (!day) return <div key={ci} />;
                      const iso = day.format('YYYY-MM-DD');
                      const list = byDay[iso] ?? [];
                      const done = list.filter((s) => s.status === 'COMPLETED').length;
                      const missed = list.filter((s) => s.status === 'MISSED').length;
                      const isToday = iso === today;
                      const sunday = day.day() === 0;
                      return (
                        <button
                          key={ci}
                          onClick={() => { setWeekStart(day.startOf('week').add(1, 'day')); setView('week'); }}
                          title={list.length ? `${list.length} planned · ${done} done · ${missed} missed — open this week` : 'Nothing planned — open this week'}
                          className={`min-h-[60px] rounded-md border p-1 text-left transition
                            ${isToday ? 'border-brand-500 ring-1 ring-brand-500/20' : 'border-line'}
                            ${sunday ? 'bg-surface-3' : 'bg-surface-2'} hover:border-brand-500`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={isToday ? 'type-caption-strong text-brand-700 dark:text-brand-300' : 'type-caption text-subtle'}>
                              {day.format('D')}
                            </span>
                            {list.length > 0 && <span className="type-caption font-semibold">{list.length}</span>}
                          </div>
                          {list.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-0.5">
                              {done > 0 && <span className={`h-1.5 w-4 rounded-full ${URGENCY_BAR.done}`} />}
                              {missed > 0 && <span className={`h-1.5 w-4 rounded-full ${URGENCY_BAR.overdue}`} />}
                              {list.length - done - missed > 0 && <span className={`h-1.5 w-4 rounded-full ${URGENCY_BAR.planned}`} />}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
                <p className="mt-1 type-caption text-subtle">
                  Tap a day to open its week and plan there. Sundays are shaded — the generator skips them.
                </p>
              </div>
            ) : (
            /* ── The week ─────────────────────────────────────────────────── */
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
              {days.map((day) => {
                const iso = day.format('YYYY-MM-DD');
                const list = byDay[iso] ?? [];
                const isToday = iso === today;
                return (
                  <div key={iso} className={`rounded-lg border ${isToday ? 'border-brand-500 ring-1 ring-brand-500/20' : 'border-line'} bg-surface-2`}>
                    <div className={`flex items-center justify-between border-b px-2 py-1.5 ${isToday ? 'border-brand-500/40' : 'border-line'}`}>
                      <div>
                        <div className="type-label">{day.format('ddd')}{isToday && <span className="ml-1 text-brand-600">•</span>}</div>
                        <div className="type-caption text-subtle">{day.format('DD MMM')}</div>
                      </div>
                      {list.length > 0 && <Pill color={list.length > 8 ? 'orange' : 'default'}>{list.length}</Pill>}
                    </div>
                    <div className="min-h-[80px] space-y-1 p-1.5">
                      {stopsLoading && <div className="p-2 text-center"><Spin size="small" /></div>}
                      {list.map((s) => {
                        const p = STOP_STATUS[s.status] ?? STOP_STATUS.PLANNED;
                        const bar = s.status === 'COMPLETED' ? URGENCY_BAR.done
                          : s.status === 'MISSED' ? URGENCY_BAR.overdue
                          : s.status === 'STARTED' ? URGENCY_BAR.soon : URGENCY_BAR.planned;
                        return (
                          <div key={s.id} className="group flex gap-1.5 rounded-md border border-line bg-surface p-1.5">
                            <span className={`w-0.5 shrink-0 rounded-full ${bar}`} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start gap-1">
                                <span className="min-w-0 flex-1 truncate type-caption font-medium" title={s.customerName}>
                                  {s.customerName}
                                </span>
                                {canSchedule && s.status !== 'COMPLETED' && s.status !== 'CANCELLED' && (
                                  <button
                                    aria-label={`Remove ${s.customerName} from ${day.format('DD MMM')}`}
                                    onClick={() => delMut.mutate(s.id)}
                                    className="shrink-0 rounded text-subtle opacity-0 transition hover:text-danger-600 focus:opacity-100 group-hover:opacity-100"
                                  >
                                    <X className="size-3.5" />
                                  </button>
                                )}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <Pill color={p.color}>{p.label}</Pill>
                                {s.orderPlaced && <Pill color="green">order</Pill>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {!list.length && !stopsLoading && <div className="p-2 text-center type-caption text-subtle">—</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

function LegendDot({ cls, label }: { cls: string; label: string }) {
  return <span className="inline-flex items-center gap-1"><span className={`size-2 rounded-full ${cls}`} />{label}</span>;
}

/* ────────────────────────────────────────────────────────────────────────────
 * VISIT SCHEDULER — the rules, and what they will cost.
 * ──────────────────────────────────────────────────────────────────────────── */
function SchedulerTab({ canPlan, territoryOptions, presetTerritoryId, onPresetUsed }: {
  canPlan: boolean;
  territoryOptions: ReturnType<typeof useStaffTerritories>;
  /** A territory carried in from the Coverage tab's "Set a rule" — the one whose customers were
   *  found to have none. Null when the reader opened this tab on their own. */
  presetTerritoryId?: string | null;
  onPresetUsed?: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tState, setTState] = useState<string | null>(null);
  const [tDistrict, setTDistrict] = useState<string | null>(null);
  const [tBeat, setTBeat] = useState<string | null>(null);
  const [audience, setAudience] = useState<Audience>('RETAILER');
  const [freq, setFreq] = useState<number>(2);
  const [customFreq, setCustomFreq] = useState<number>(2);
  const [periodKey, setPeriodKey] = useState('month');
  const [minMinutes, setMinMinutes] = useState(5);
  const [requireGeofence, setRequireGeofence] = useState(false);
  const [note, setNote] = useState('');

  const territoryId = tBeat ?? tDistrict ?? tState ?? undefined;
  const period = PERIOD.find((p) => p.key === periodKey)!;
  const timesPerCycle = freq === 0 ? customFreq : freq;

  const { data: policies, isLoading } = useQuery({
    queryKey: ['coverage-policies'],
    queryFn: () => request<any[]>({ url: '/coverage/policies' }),
  });

  // Asked as the dropdowns change, so the cost of the rule is on screen BEFORE it is committed.
  const { data: feas } = useQuery({
    queryKey: ['coverage-feasibility', territoryId, audience, timesPerCycle, period.key],
    queryFn: () => request<any>({
      url: '/coverage/policies/feasibility',
      params: { territoryId: territoryId!, audience, timesPerCycle: String(timesPerCycle), cycleLength: String(period.cycleLength), cycleUnit: period.cycleUnit },
    }),
    enabled: open && !!territoryId,
  });

  const close = () => { setNote(''); setOpen(false); };
  const saveMut = useConfirmedMutation({ title: "Save this visit rule?", content: "Coverage is recalculated from it immediately, so overdue counts will change.", okText: "Yes, save" }, {
    mutationFn: () => request<any>({
      url: '/coverage/policies', method: 'POST',
      data: { territoryId, audience, timesPerCycle, cycleLength: period.cycleLength, cycleUnit: period.cycleUnit, minVisitMinutes: minMinutes, requireGeofence, note: note.trim() || undefined },
    }),
    onSuccess: (r: any) => {
      message.success(r?.previousLabel
        ? `Visit schedule changed from ${r.previousLabel} to ${r.normLabel}.`
        : 'Visit schedule saved. Coverage is recalculated from it immediately.');
      close();
      qc.invalidateQueries({ queryKey: ['coverage-policies'] });
      qc.invalidateQueries({ queryKey: ['coverage-policy-history'] });
      qc.invalidateQueries({ queryKey: ['coverage-rows'] });
      qc.invalidateQueries({ queryKey: ['coverage-summary'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not save this rule.'),
  });

  const archiveMut = useConfirmedMutation({ title: "Retire this visit rule?", content: "It stops driving coverage. Past visits and history are untouched.", okText: "Yes, retire", danger: true }, {
    mutationFn: (id: string) => request({ url: `/coverage/policies/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Visit schedule retired.');
      qc.invalidateQueries({ queryKey: ['coverage-policies'] });
      qc.invalidateQueries({ queryKey: ['coverage-policy-history'] });
      qc.invalidateQueries({ queryKey: ['coverage-rows'] });
    },
  });

  /*
   * Arriving from "No Rule: 3 → Set a rule". The territory travels in the URL, so the form opens
   * already pointed at it instead of making the reader re-find it in a cascade of thirty states —
   * the gap was found on another tab and the fix should not start from scratch.
   *
   * The cascade wants the whole state/district/beat chain, so walk UP the tree from whatever level
   * was clicked. The param is consumed once, on arrival: leaving it in the URL would re-open the
   * form every time the reader closed it.
   */
  useEffect(() => {
    if (!presetTerritoryId || !canPlan) return;
    const chain: any[] = [];
    let cur = territoryOptions.nodes.get(String(presetTerritoryId));
    // Nothing to preset if the tree has not loaded yet — the effect re-runs when it has.
    if (!cur) return;
    for (let hop = 0; cur && hop < 8; hop++) { chain.push(cur); cur = cur.parentId ? territoryOptions.nodes.get(cur.parentId) : undefined; }
    setTState(chain.find((n) => n.level === 'state')?.id ?? null);
    setTDistrict(chain.find((n) => n.level === 'district')?.id ?? null);
    setTBeat(chain.find((n) => n.level === 'beat')?.id ?? null);
    setOpen(true);
    onPresetUsed?.();
  }, [presetTerritoryId, canPlan, territoryOptions.nodes]);

  const cols: DataTableColumn<any>[] = [
    {
      key: 'territoryName', title: 'Territory', dataIndex: 'territoryName', alwaysVisible: true, width: 248,
      render: (v: any, r: any) => (
        <span className="flex items-center gap-1.5">
          <span className="min-w-0 break-words font-medium">{v}</span>
          <TerritoryLevelTag level={r.territoryLevel} className="shrink-0" />
          {r.inherited && (
            <Tooltip title={`Set on ${v}, above the area you manage. It applies to your customers wherever no rule of your own overrides it, and only someone who manages ${v} can change it.`}>
              <span className="shrink-0"><Pill color="default">Inherited</Pill></span>
            </Tooltip>
          )}
        </span>
      ),
    },
    { key: 'audience', title: 'Applies To', dataIndex: 'audience', render: (v: any) => (v === 'BOTH' ? 'Retailers & farmers' : v === 'RETAILER' ? 'Retailers' : 'Farmers') },
    { key: 'normLabel', title: 'Visit Frequency', dataIndex: 'normLabel', render: (v: any) => <span className="font-semibold">{v}</span> },
    { key: 'minVisitMinutes', title: 'Min. Minutes', dataIndex: 'minVisitMinutes', align: 'right' },
    { key: 'requireGeofence', title: 'Geofence Required', dataIndex: 'requireGeofence', align: 'center', render: (v: any) => (v ? <Pill color="green">yes</Pill> : <span className="text-subtle">no</span>) },
    { key: 'effectiveFrom', title: 'Effective From', dataIndex: 'effectiveFrom', render: (v: any) => formatIstDate(v) },
    {
      /* A rule set part-way through a window is measured from the NEXT one; without this column the reader saw
         "Effective From 15 Sep" and every customer still read "No Visit Schedule" until 1 Oct. */
      key: 'countsFrom',
      title: (
        <span className="inline-flex items-center gap-1">
          Counts From
          <InfoTooltip label="The first full visit window this rule is measured in. A rule set part-way through a week or month starts counting at the next one." ariaLabel='What "Counts From" means' size={13} />
        </span>
      ),
      dataIndex: 'countsFrom',
      render: (v: any, r: any) => (v
        ? <span className={v !== r.effectiveFrom ? 'font-medium' : undefined}>{formatIstDate(v)}</span>
        : <span className="text-subtle">—</span>),
    },
    { key: 'createdByName', title: 'Set By', dataIndex: 'createdByName', defaultVisible: false },
    { key: 'note', title: 'Reason', dataIndex: 'note', ellipsis: true, defaultVisible: false },
    ...(canPlan ? [{
      key: 'actions', title: '', align: 'right' as const, width: 48, alwaysVisible: true,
      render: (_: any, r: any) => r.inherited ? null : (
        <Tooltip title="Retire this rule">
          <button aria-label="Retire this rule" onClick={() => archiveMut.mutate(r.id)}
            className="inline-flex size-8 items-center justify-center rounded-md text-subtle hover:bg-surface-3 hover:text-danger-600">
            <X className="size-4" />
          </button>
        </Tooltip>
      ),
    }] : []),
  ];

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="type-section-title">Visit Scheduler</div>
          <p className="type-body text-muted">
            A rule set on a state or district applies to every beat under it. A deeper rule overrides a shallower one.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* "Rule history", not "Change history": it lists the VERSIONS OF THESE RULES — what each
              one used to say, who changed it and when — and it sits beside "Set a rule", so the two
              buttons name the same subject. "Change history" could be the audit log of anything. */}
          <Button size="sm" icon={<History className="size-4" />} onClick={() => setHistoryOpen(true)}>Rule history</Button>
          {canPlan && <Button variant="primary" size="sm" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>Set a rule</Button>}
        </div>
      </div>

      <div className="mt-3">
        <DataTable
          storageKey="coverage-policies"
          rowKey={(r: any) => String(r.id)}
          columns={cols}
          dataSource={policies ?? []}
          loading={isLoading}
          pagination={{ pageSize: 20 }}
          emptyText={(
            <EmptyState
              icon={<ClipboardList className="size-8" />}
              title="No Visit Schedule configured"
              description="Create a Visit Scheduler rule for this territory to start tracking customer coverage."
              action={canPlan ? <Button size="sm" variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>Set a rule</Button> : undefined}
            />
          )}
        />
      </div>

      <Modal
        open={open} onOpenChange={(o) => { if (!o) close(); }}
        title="Set a visit schedule" width={760}
        footer={(
          <>
            <Button variant="default" onClick={close}>Cancel</Button>
            <Button variant="primary" disabled={!territoryId} loading={saveMut.isPending}
              onClick={() => territoryId && saveMut.mutate()}>Save rule</Button>
          </>
        )}
      >
        <div className="grid gap-4 md:grid-cols-[1fr_264px]">
          {/* ── The choices ─────────────────────────────────────────────── */}
          <div className="space-y-4">
            <div>
              <div className="type-label mb-1">Territory</div>
              <div className="flex flex-wrap gap-2">
                <Select
                  value={tState ?? undefined} placeholder="State" allowClear className="min-w-[130px] flex-1"
                  onChange={(v: any) => { setTState(v ?? null); setTDistrict(null); setTBeat(null); }}
                  options={territoryOptions.optionsAt('state', null)}
                />
                <Select
                  value={tDistrict ?? undefined} placeholder="District (optional)" allowClear className="min-w-[150px] flex-1"
                  disabled={!tState}
                  onChange={(v: any) => { setTDistrict(v ?? null); setTBeat(null); }}
                  options={tState ? territoryOptions.optionsAt('district', tState) : []}
                />
                <Select
                  value={tBeat ?? undefined} placeholder="Beat (optional)" allowClear className="min-w-[130px] flex-1"
                  disabled={!tDistrict}
                  onChange={(v: any) => setTBeat(v ?? null)}
                  options={tDistrict ? territoryOptions.optionsAt('beat', tDistrict) : []}
                />
              </div>
              <p className="type-caption mt-1 text-subtle">Leave district and beat empty to cover the whole state.</p>
            </div>

            <div>
              <div className="type-label mb-1">Applies to</div>
              <Segmented
                value={audience}
                onChange={(v: any) => setAudience(v)}
                options={[
                  { value: 'RETAILER', label: 'Retailers' },
                  { value: 'FARMER', label: 'Farmers' },
                  { value: 'BOTH', label: 'Both' },
                ]}
              />
            </div>

            {/* The two dropdowns. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="type-label mb-1">Visit frequency</div>
                <Select value={freq} onChange={(v: any) => setFreq(Number(v))} options={FREQUENCY} className="w-full" />
                {freq === 0 && (
                  <InputNumber className="mt-2 w-full" min={1} max={60} value={customFreq}
                    onChange={(v: any) => setCustomFreq(Number(v) || 1)} addonAfter="visits" />
                )}
              </div>
              <div>
                <div className="type-label mb-1">Per</div>
                <Select value={periodKey} onChange={(v: any) => setPeriodKey(String(v))}
                  options={PERIOD.map((p) => ({ value: p.key, label: p.label }))} className="w-full" />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="type-label mb-1">Minimum visit length</div>
                <InputNumber min={0} max={240} value={minMinutes} onChange={(v: any) => setMinMinutes(Number(v) || 0)}
                  addonAfter="minutes" className="w-full" />
                <p className="type-caption mt-1 text-subtle">A shorter call does not count toward the rule.</p>
              </div>
              <div>
                <div className="type-label mb-1">Require geofence</div>
                <Switch checked={requireGeofence} onCheckedChange={(v) => setRequireGeofence(!!v)} aria-label="Require geofence" />
                <p className="type-caption mt-1 text-subtle">
                  {requireGeofence ? 'Only in-geofence check-ins count.' : 'Out-of-geofence visits count, but stay flagged.'}
                </p>
              </div>
            </div>

            <div>
              <div className="type-label mb-1">Reason for this change <span className="font-normal text-subtle">(optional)</span></div>
              <Input value={note} onChange={(e: any) => setNote(e.target.value)}
                placeholder="e.g. reduced after the monsoon season" />
            </div>
          </div>

          {/* ── The preview: what this rule will actually cost ───────────── */}
          <RulePreview
            territoryName={territoryId ? territoryOptions.nameOf(territoryId) : null}
            audience={audience}
            ruleLabel={`${timesPerCycle} visit${timesPerCycle === 1 ? '' : 's'} per ${period.label.toLowerCase()}`}
            feas={territoryId ? feas : null}
          />
        </div>
      </Modal>

      <RuleHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </Card>
  );
}

/**
 * What the rule will demand, before it is saved.
 *
 * A frequency on its own is abstract — "twice a month" sounds modest until it is 536 calls across
 * four people. This turns the two dropdowns into a number someone can accept or refuse.
 */
function RulePreview({ territoryName, audience, ruleLabel, feas }: {
  territoryName: string | null; audience: Audience; ruleLabel: string; feas: any;
}) {
  const VERDICT: Record<string, { pill: string; head: string; body: string }> = {
    OK: { pill: 'green', head: 'GREEN — Comfortable', body: 'This is a workable load.' },
    TIGHT: { pill: 'orange', head: 'AMBER — Tight', body: 'Achievable, but it leaves little room for travel or a bad day.' },
    IMPOSSIBLE: { pill: 'red', head: 'RED — Not achievable', body: 'Nobody can walk this. Reduce the frequency or add staff.' },
    UNSTAFFED: { pill: 'red', head: 'RED — No staff assigned', body: 'No active staff are assigned to this territory.' },
    EMPTY: { pill: 'default', head: 'No customers', body: 'No customers sit under this territory yet.' },
  };
  const v = feas ? VERDICT[String(feas.verdict)] : null;

  const Row = ({ label, value, strong }: { label: string; value: any; strong?: boolean }) => (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="type-caption text-subtle">{label}</span>
      <span className={`text-right type-body ${strong ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  );

  return (
    <aside className="rounded-lg border border-line bg-surface-2 p-3">
      <div className="type-label mb-2">Visit Scheduler Preview</div>
      {!territoryName ? (
        <p className="type-caption text-subtle">Pick a territory to see what this rule would demand.</p>
      ) : (
        <div className="divide-y divide-line">
          <Row label="Territory" value={territoryName} strong />
          <Row label="Audience" value={audience === 'BOTH' ? 'Retailers & farmers' : audience === 'RETAILER' ? 'Retailers' : 'Farmers'} />
          <Row label="Rule" value={ruleLabel} strong />
          {feas ? (
            <>
              <Row label="Customers" value={Number(feas.customers).toLocaleString('en-IN')} />
              <Row label="Assigned staff" value={feas.reps} />
              <Row label="Required visits" value={`${Number(feas.visitsPerCycle).toLocaleString('en-IN')} / cycle`} strong />
              <Row label="Working days" value={feas.workingDays} />
              <Row label="Average load" value={`${feas.perRepPerDay} visits/person/day`} strong />
              <div className="pt-2">
                <div className="type-caption text-subtle">Feasibility</div>
                <div className="mt-1 flex items-start gap-2">
                  {v?.pill === 'red'
                    ? <TriangleAlert className="mt-0.5 size-4 shrink-0 text-danger-600" />
                    : <MapPin className="mt-0.5 size-4 shrink-0 text-subtle" />}
                  <div>
                    <Pill color={v?.pill ?? 'default'}>{v?.head ?? '—'}</Pill>
                    <p className="mt-1 type-caption text-subtle">{v?.body}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-3 text-center"><Spin size="small" /></div>
          )}
        </div>
      )}
    </aside>
  );
}

/**
 * Who changed a rule, when, and to what.
 *
 * Read from the POLICY ROWS themselves — superseding a rule writes a new row and archives the old
 * one, so the table already IS the trail. Nothing is reconstructed from logs.
 */
/**
 * WHAT ACTUALLY CHANGED between two versions of a rule.
 *
 * The dialog printed a bare "was <frequency>" against every superseded version, so a rule re-saved
 * with the same frequency read "2× / month · was 2× / month" — a change announced where there was
 * none, next to a real one that looked identical. A rule carries three things a person can change,
 * so all three are compared and only the ones that moved are named; a version that changed nothing
 * says so, which is a fact about how the rule was administered rather than a gap to be hidden.
 */
function describeRuleChange(now: any, before: any): string {
  const bits: string[] = [];
  if (now.normLabel !== before.normLabel) bits.push(`was ${before.normLabel}`);
  if (Number(now.minVisitMinutes ?? 0) !== Number(before.minVisitMinutes ?? 0)) {
    bits.push(`minimum ${before.minVisitMinutes} → ${now.minVisitMinutes} min`);
  }
  if (!!now.requireGeofence !== !!before.requireGeofence) {
    bits.push(now.requireGeofence ? 'geofence now required' : 'geofence no longer required');
  }
  return bits.length ? bits.join(' · ') : 'reissued — nothing changed';
}

function RuleHistoryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['coverage-policy-history'],
    queryFn: () => request<any[]>({ url: '/coverage/policies/history' }),
    enabled: open,
  });

  // Group by territory + audience so a rule's own lineage reads top to bottom.
  const groups = useMemo(() => {
    const m = new Map<string, { title: string; rows: any[] }>();
    for (const r of data ?? []) {
      const key = `${r.territoryId}|${r.audience}`;
      const g = m.get(key) ?? {
        title: `${r.territoryName} · ${r.audience === 'BOTH' ? 'Retailers & farmers' : r.audience === 'RETAILER' ? 'Retailers' : 'Farmers'}`,
        rows: [] as any[],
      };
      g.rows.push(r);
      m.set(key, g);
    }
    return [...m.values()];
  }, [data]);

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }}
      title="Rule history" width={620}
      footer={<Button variant="default" onClick={onClose}>Close</Button>}>
      {isLoading ? (
        <div className="p-8 text-center"><Spin /></div>
      ) : !groups.length ? (
        <EmptyState icon={<History className="size-8" />} title="No rule has been changed yet"
          description="Every version of a rule is kept — what it used to say, who changed it and when. They will appear here, newest first." />
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.title}>
              <h3 className="type-label mb-2">{g.title}</h3>
              <ol className="space-y-2">
                {g.rows.map((r, i) => {
                  const previous = g.rows[i + 1];
                  return (
                    <li key={r.id} className="rounded-lg border border-line p-2.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <Pill color={r.status === 'ACTIVE' ? 'green' : 'default'}>{r.status === 'ACTIVE' ? 'Current' : 'Superseded'}</Pill>
                        <span className="type-body font-semibold">{r.normLabel}</span>
                        {previous && <span className="type-caption text-subtle">{describeRuleChange(r, previous)}</span>}
                      </div>
                      <div className="mt-1 type-caption text-subtle">
                        {/* Superseding a rule ends the old one the day BEFORE the new one starts, so a
                            rule replaced on the day it was set ends before it began: "03 Sep – 02 Sep".
                            The dates are right and the sentence is impossible, so it is said in words
                            instead — that version never governed a window. */}
                        {r.effectiveTo && String(r.effectiveTo) < String(r.effectiveFrom)
                          ? `${formatIstDate(r.effectiveFrom)} · replaced the same day, so it never covered a window`
                          : `${formatIstDate(r.effectiveFrom)}${r.effectiveTo ? ` – ${formatIstDate(r.effectiveTo)}` : ' – ongoing'}`}
                        {r.changedBy ? ` · by ${r.changedBy}${r.changedByRole ? ` (${r.changedByRole})` : ''}` : ''}
                      </div>
                      {r.note && <div className="mt-1 type-caption">Reason: {r.note}</div>}
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** A customer's own visit record — the "why is this overdue?" answer, without leaving the page. */
function CustomerHistoryModal({ target, onClose }: { target: any | null; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['coverage-customer-visits', target?.customerType, target?.customerId],
    queryFn: () => request<any[]>({
      url: '/visits',
      params: target.customerType === 'RETAILER' ? { retailerId: target.customerId } : { farmerId: target.customerId },
    }),
    enabled: !!target,
  });

  return (
    <Modal open={!!target} onOpenChange={(o) => { if (!o) onClose(); }} width={720}
      title={<span className="flex items-center gap-2">{target?.name ?? ''}{target?.normLabel && <Pill color="default">{target.normLabel}</Pill>}</span>}
      footer={<Button variant="default" onClick={onClose}>Close</Button>}>
      {target && (
        <div className="space-y-3">
          {/* WHO — identity and ownership, before any numbers. */}
          <div className="flex flex-wrap items-center gap-2">
            <Pill color={target.customerType === 'RETAILER' ? 'blue' : 'lime'}>
              {target.customerType === 'RETAILER' ? 'Retailer' : 'Farmer'}
            </Pill>
            {target.code && <span className="type-caption text-subtle">{target.code}</span>}
            {target.place && <span className="type-caption text-subtle">· {target.place}</span>}
            {target.territoryName && <span className="type-caption text-subtle">· {target.territoryName}</span>}
            <span className="ml-auto">
              <Pill color={(COVERAGE_STATE[target.state] ?? COVERAGE_STATE.NO_NORM).color}>
                {(COVERAGE_STATE[target.state] ?? COVERAGE_STATE.NO_NORM).label}
              </Pill>
            </span>
          </div>

          {/* THE THREE NUMBERS that decide whether to go, given their own weight. */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg border border-line bg-surface-2 p-2.5">
              <div className="type-caption text-subtle">This window</div>
              <div className="type-body font-semibold">
                {target.policyId ? `${target.done}/${target.required}` : '—'}
              </div>
              {target.policyId && (
                <>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-3">
                    <div className="h-full rounded-full bg-brand-500"
                      style={{ width: `${Math.min(100, Math.round((target.done / Math.max(1, target.required)) * 100))}%` }} />
                  </div>
                  {target.cycleStart && target.cycleEnd && (
                    <div className="mt-1 type-caption text-subtle">
                      {formatIstDate(target.cycleStart)} – {formatIstDate(target.cycleEnd)}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="rounded-lg border border-line bg-surface-2 p-2.5">
              <div className="type-caption text-subtle">Last visit</div>
              <div className="type-body font-semibold">
                {target.lastVisitAt ? formatIstDate(target.lastVisitAt) : 'Never'}
              </div>
              {target.daysSinceLast != null && (
                <div className="mt-1 type-caption text-subtle">{target.daysSinceLast} days ago</div>
              )}
            </div>
            <div className="rounded-lg border border-line bg-surface-2 p-2.5">
              <div className="type-caption text-subtle">Next due</div>
              <div className="type-body font-semibold"><OverdueCell days={target.overdueDays} /></div>
              {target.intervalDays != null && (
                <div className="mt-1 type-caption text-subtle">every {target.intervalDays}d</div>
              )}
            </div>
          </div>

          {/* Ownership and commercial context. */}
          <div className="grid grid-cols-2 gap-2 rounded-lg border border-line bg-surface-2 p-3 sm:grid-cols-3">
            <Fact label="Coverage rule" value={target.normLabel ?? 'No rule'} />
            <Fact
              label="Assigned staff"
              value={target.repName
                ? <span className="inline-flex items-center gap-1.5">{target.repName}<RoleBadge role={target.repRole} /></span>
                : <Pill color="orange">Unassigned</Pill>}
            />
            <Fact label="Visits in window" value={target.policyId ? target.visitsInCycle : '—'} />
            <Fact label="Last order" value={target.lastOrderAt
              ? formatIstDate(target.lastOrderAt)
              : (target.salesLinked === false ? 'No linked sales' : 'None yet')} />
            <Fact label="Last order value" value={target.lastOrderAmount
              ? inr(target.lastOrderAmount)
              : (target.salesLinked === false ? 'No linked sales' : '—')} />
            {target.exemptReason
              ? <Fact label="Exempt reason" value={target.exemptReason} />
              : <Fact label="Days since visit" value={target.daysSinceLast != null ? `${target.daysSinceLast}d` : 'never visited'} />}
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="type-label">Visit history</span>
          {!!data?.length && <span className="type-caption text-subtle">{data.length} recorded</span>}
        </div>
        {isLoading ? (
          <div className="p-8 text-center"><Spin /></div>
        ) : !data?.length ? (
          <EmptyState compact icon={<MapPin className="size-7" />} title="No visits recorded"
            description="This customer has never been visited." />
        ) : (
          <ol className="space-y-2">
            {data.map((v: any) => {
              const q = v.quality;
              return (
                <li key={v.id} className="rounded-lg border border-line p-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="type-body font-medium">
                      {v.checkInAt ? formatIstDate(v.checkInAt) : '—'}
                      {v.checkInAt && <span className="ml-1.5 type-caption text-subtle">{formatIstTime(v.checkInAt)}</span>}
                    </span>
                    <div className="flex flex-wrap items-center gap-1">
                      {v.durationMin != null && <Pill color="default">{v.durationMin} min</Pill>}
                      {v.photosCount > 0 && <Pill color="default">{v.photosCount} photo{v.photosCount === 1 ? '' : 's'}</Pill>}
                      {v.orderPlaced ? <Pill color="green">order</Pill> : null}
                      {v.checkInWithinGeofence === false && <Pill color="red">outside geofence</Pill>}
                      {/* Quality is the shared score (docs/VISIT-DATA-CONTRACT.md §5) — the same
                          number the performance dashboard averages, never a second opinion. */}
                      {q && <Pill color={q.score >= 75 ? 'green' : q.score >= 50 ? 'gold' : 'red'}>{q.score}%</Pill>}
                    </div>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 type-caption text-subtle">
                    <span>{v.staffName ?? 'staff'}</span>
                    {v.staffRole && <RoleBadge role={v.staffRole} />}
                    {v.visitNo && <span>· {v.visitNo}</span>}
                  </div>
                  {q && (
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 type-caption">
                      <QSignal ok={q.gpsVerified} label="GPS" />
                      <QSignal ok={q.photoUploaded} label="Photo" />
                      <QSignal ok={q.durationAchieved} label="Duration" />
                      <QSignal ok={q.feedbackCaptured} label="Notes" />
                      <QSignal ok={q.orderGenerated} label="Order" />
                    </div>
                  )}
                  {v.notes && <div className="mt-1.5 rounded-md bg-surface-2 p-2 type-caption">{v.notes}</div>}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </Modal>
  );
}

/** One quality signal, present or not — a tick reads faster than a sentence. */
function QSignal({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${ok ? 'text-brand-600 dark:text-brand-400' : 'text-subtle'}`}>
      {ok ? <CheckCircle2 className="size-3.5" /> : <X className="size-3.5" />}{label}
    </span>
  );
}

function Fact({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="type-caption text-subtle">{label}</div>
      <div className="type-body font-medium">{value}</div>
    </div>
  );
}

/**
 * Give an unowned customer a rep, from the row that shows they have none.
 *
 * Writes through the CUSTOMER's own endpoint (`PATCH /retailers/:id` · `PATCH /farmers/:id`), which
 * already carries the rule that matters: the server refuses a rep who does not work that customer's
 * territory. Rather than let someone discover that as a 400, the picker separates the two groups —
 * reps who cover this territory are offered first, the rest are listed but disabled with the reason.
 *
 * Ownership is otherwise DERIVED from territory assignments, so this is a deliberate override of a
 * cached owner, not a new source of truth. Getting the territory right remains the real fix, and the
 * dialog says so.
 */
function AssignStaffModal({ target, staff, terr, onClose, onDone }: {
  target: any | null; staff: any[]; terr: ReturnType<typeof useStaffTerritories>;
  onClose: () => void; onDone: () => void;
}) {
  const [userId, setUserId] = useState<string | undefined>();

  useEffect(() => { setUserId(target?.repId ? String(target.repId) : undefined); }, [target]);

  /*
   * ONLY the staff who can actually be assigned.
   *
   * The first cut offered everyone and greyed the ineligible — which meant a dropdown of nine
   * entries where eight were un-pickable, in a popover that escaped the modal. A list is not a
   * reference table: showing choices that cannot be chosen is noise, and noise in a two-field dialog
   * is most of the dialog.
   *
   * The excluded ones are still ACCOUNTED FOR in a single line below, so the absence is explained
   * rather than mysterious.
   */
  const { eligible, blockedCount } = useMemo(() => {
    const covers = (id: any) => (target?.territoryId ? terr.matches(id, String(target.territoryId)) : true);
    const all = [...staff].map((u) => ({
      value: String(u.id),
      name: String(u.fullName ?? ''),
      role: (u.role?.code ?? u.roleCode ?? null) as string | null,
      covers: covers(u.id),
    }));
    const ok = all.filter((o) => o.covers).sort((a, b) => a.name.localeCompare(b.name));
    return { eligible: ok, blockedCount: all.length - ok.length };
  }, [staff, terr, target]);

  // A search box earns its place only once the list stops fitting on screen.
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? eligible.filter((o) => o.name.toLowerCase().includes(needle)) : eligible;
  }, [eligible, q]);
  useEffect(() => { setQ(''); }, [target]);

  const save = useConfirmedMutation({ title: "Assign this customer to the selected rep?", content: "They move onto that rep’s coverage immediately.", okText: "Yes, assign" }, {
    mutationFn: () => request({
      url: target.customerType === 'RETAILER' ? `/retailers/${target.customerId}` : `/farmers/${target.customerId}`,
      method: 'PATCH',
      data: { assignedSalesPersonId: userId },
    }),
    onSuccess: () => { message.success('Assigned. Coverage recalculates from it immediately.'); onDone(); onClose(); },
    // The territory guard's refusal is the useful message — show it verbatim rather than "failed".
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not assign this customer.'),
  });

  return (
    <Modal
      open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Assign staff — ${target?.name ?? ''}`} width={480}
      footer={(
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!userId || userId === String(target?.repId ?? '')} loading={save.isPending}
            onClick={() => save.mutate()}>Save</Button>
        </>
      )}
    >
      <div className="space-y-3">
        <div className="type-caption text-subtle">
          {target?.territoryName
            ? <>Territory: <span className="font-medium text-fg">{target.territoryName}</span></>
            : 'This customer has no territory set.'}
        </div>

        {/* Picked IN PLACE, not from a popover. With a handful of eligible people a dropdown is
            pure overhead — and its menu escaped the dialog, which is what made this look broken. */}
        {eligible.length === 0 ? (
          <EmptyState
            compact
            icon={<Users className="size-7" />}
            title="Nobody works this territory yet"
            description={target?.territoryName
              ? `No staff are assigned to ${target.territoryName}, so there is nobody who can own this customer. Assign someone to the territory first — Masters → Territories → Coverage.`
              : 'This customer has no territory, so no staff member can be matched to it. Set its territory first.'}
          />
        ) : (
          <div>
            <div className="type-label mb-1">Staff who work this territory</div>
            {eligible.length > 6 && (
              <Input
                value={q} onChange={(e: any) => setQ(e.target.value)} allowClear
                placeholder="Search staff" prefix={<Search className="size-4 text-subtle" />}
                className="mb-2"
              />
            )}
            <div role="radiogroup" aria-label="Staff who work this territory"
              className="max-h-[260px] divide-y divide-line overflow-y-auto rounded-lg border border-line">
              {shown.map((o) => {
                const active = userId === o.value;
                return (
                  <button
                    key={o.value} role="radio" aria-checked={active}
                    onClick={() => setUserId(o.value)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left transition
                      ${active ? 'bg-brand-500/10' : 'hover:bg-surface-2'}`}
                  >
                    <span className={`size-4 shrink-0 rounded-full border-2 ${active ? 'border-brand-600 bg-brand-600 ring-2 ring-inset ring-surface' : 'border-line-strong'}`} />
                    <span className="min-w-0 flex-1 truncate type-body">{o.name}</span>
                    <RoleBadge role={o.role} />
                    {String(target?.repId ?? '') === o.value && <Pill color="default">current</Pill>}
                  </button>
                );
              })}
              {!shown.length && (
                <div className="px-3 py-4 text-center type-caption text-subtle">No staff match “{q}”.</div>
              )}
            </div>
          </div>
        )}

        {blockedCount > 0 && (
          <p className="type-caption text-subtle">
            {blockedCount} other staff {blockedCount === 1 ? 'member does' : 'members do'} not work this
            territory and cannot be assigned to it.
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Put one customer on someone's day, straight from the coverage register. */
function ScheduleVisitModal({ target, staff, me, onClose, onDone }: {
  target: any | null; staff: any[]; me: any; onClose: () => void; onDone: () => void;
}) {
  const [userId, setUserId] = useState<string | undefined>();
  const [date, setDate] = useState<Dayjs>(dayjs());

  /*
   * Default to the customer's own rep — the person answerable for the beat.
   *
   * It used to fall back to the SIGNED-IN user, which put a raw "69" in the field whenever an admin
   * opened this on an unassigned customer: an Admin or Super Admin is not field staff, so their id
   * is not among the options and the Select had nothing to render but the value itself. A fallback
   * that can name somebody who cannot be chosen is worse than no fallback — so it now only
   * pre-fills when that person is actually in the list.
   */
  const inStaffList = (id?: string | null) => !!id && staff.some((u) => String(u.id) === String(id));
  const effectiveUser = userId
    ?? (inStaffList(target?.repId) ? String(target.repId)
      : inStaffList(me?.id) ? String(me.id)
        : undefined);

  const save = useConfirmedMutation({ title: "Schedule this visit?", okText: "Yes, schedule" }, {
    mutationFn: () => request({
      url: '/coverage/schedule', method: 'POST',
      data: {
        userId: effectiveUser, planDate: date.format('YYYY-MM-DD'),
        customerType: target.customerType, customerId: target.customerId,
      },
    }),
    onSuccess: () => {
      message.success(`Visit planned for ${date.format('DD MMM')}.`);
      onDone(); onClose(); setUserId(undefined); setDate(dayjs());
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Could not schedule that visit.'),
  });

  return (
    <Modal
      open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Schedule a visit — ${target?.name ?? ''}`} width={460}
      footer={(
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!effectiveUser} loading={save.isPending} onClick={() => save.mutate()}>
            Add to plan
          </Button>
        </>
      )}
    >
      <div className="space-y-3">
        <div>
          <div className="type-label mb-1">Staff member</div>
          <Select
            value={effectiveUser} showSearch optionFilterProp="name" optionLabelProp="name" className="w-full"
            placeholder="Pick a staff member" onChange={(v: any) => setUserId(v)}
            options={[...staff]
              .sort((a, b) => String(a.fullName).localeCompare(String(b.fullName)))
              .map((u) => ({
                value: String(u.id),
                name: String(u.fullName ?? ''),
                label: String(u.fullName ?? ''),
                role: (u.role?.code ?? u.roleCode ?? null) as string | null,
              }))}
            /* A list mixing Sales Persons and Sales Heads is ambiguous without the role — the same
               reason the register carries a badge beside every name. */
            optionRender={(o: any) => (
              <span className="flex w-full items-center gap-2">
                <span className="min-w-0 flex-1 truncate">{o.name}</span>
                <RoleBadge role={o.role} />
              </span>
            )}
          />
          {target?.repName && !userId && (
            <p className="type-caption mt-1 text-subtle">Defaulted to the assigned staff member.</p>
          )}
        </div>
        <div>
          <div className="type-label mb-1">Date</div>
          <DatePicker
            value={date} onChange={(d: any) => d && setDate(d)}
            disabledDate={(d: Dayjs) => d.isBefore(dayjs(), 'day')}
            className="w-full"
          />
        </div>
      </div>
    </Modal>
  );
}
